import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Check,
  Trash2,
  Pencil,
  Table,
  Info,
  Lock,
} from 'lucide-react';
import { apiLogtime, apiLoggedTime, apiPhases, apiAffaires, apiSettings } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import AffaireFilterableSelect from '../components/AffaireFilterableSelect.jsx';
import { lastAffaireIn, rememberLastAffaire } from '../lib/lastAffaire.js';
import TaskPicker from '../components/TaskPicker.jsx';
import DurationInput, { formatHHMM } from '../components/DurationInput.jsx';
import FilterBar from '../components/FilterBar.jsx';
import { useFilterStore, toFilterQuery, hasNonSelfFilter } from '../hooks/useFilterStore.js';
import { useSlidingTimeline } from '../hooks/useSlidingTimeline.js';
import {
  useRangeData,
  mergeByKey,
  mergeHolidays,
  mergePersons,
  pruneHolidays,
  spanInRange,
  dateInRange,
} from '../hooks/useRangeData.js';
import TimelineProgress from '../components/TimelineProgress.jsx';
import DensityPicker, {
  DENSITY_ROW_HEIGHT,
  readDensity,
  writeDensity,
} from '../components/DensityPicker.jsx';
import PersonFormModal from '../components/PersonFormModal.jsx';
import { dayCoverageFactor } from '../components/HalfDayPicker.jsx';
import { buildModeCursor, ToolModeBadge, ToolModeContextMenu } from '../components/ToolModeMenu.jsx';
import { MAX_HOURS_PER_DAY, normalizeHourStep, snapHours } from '../lib/hourStep.js';

/**
 * Sprint 12 — Vue Logtime calendaire (validation du prévisionnel).
 *
 * Grille calendaire (style Schedule) où chaque allocation prévisionnelle est
 * **éclatée en un square par jour ouvré**. Un square se présente :
 *  - couleur projet **translucide + bordure pointillée** → jour NON validé,
 *  - couleur projet **pleine** → jour validé (un LoggedTime existe).
 *
 * Quatre modes outil (le curseur de la souris change selon le mode ;
 * changement par clic droit) :
 *  - **Validation**   → clic sur un square non validé crée le LoggedTime
 *    (heures = prévision, source `planning`).
 *  - **Suppression**  → clic sur un square validé archive le LoggedTime et
 *    masque le square prévisionnel (ticket #23 : mode « Dévalidation » retiré).
 *  - **Modification / Création** → le curseur reflète l'état de la case :
 *    « + » (création) si aucun log, crayon (édition) si un log existe.
 *    Clic ouvre une modale complète (heures, phase, notes) ; create ou update.
 *    Fonctionne sur un jour planifié non validé ET sur une **case vide**
 *    (jour sans allocation) — la modale propose alors un sélecteur d'affaire.
 *    Les LoggedTime sans allocation source (saisie libre, import Float) sont
 *    matérialisés en case « log-only » éditable.
 *
 * Les mutations réutilisent le CRUD `/api/logged-time` (IDOR + verrou
 * comptable 423 appliqués côté backend). Le scope (USER own / MANAGER équipe /
 * ADMIN tout) est résolu par l'endpoint `/api/logtime/calendar`.
 *
 * NB : seuls les jours portés par une allocation apparaissent. Les saisies
 * manuelles sans planification restent gérées via le mode Tableau.
 */

// ---------- Ticket #91 — fusion des segments chargés ----------
const EMPTY_CALENDAR = {
  persons: [],
  allocations: [],
  loggedTimes: [],
  timeOffs: [],
  lock: null,
  holidaysByPerson: {},
  dismissedSquares: [], // Ticket #23 — squares prévisionnels masqués (Supprimer)
};
const instanceKey = (x) => x.instanceId ?? x.id;
// Tableau vide partagé : un `?? []` en ligne change d'identité à chaque rendu
// et invaliderait les mémoïsations qui en dépendent.
const EMPTY_LIST = [];
const dismissedKey = (d) =>
  `${d.personId}|${d.affaireId}|${d.phaseId ?? ''}|${d.taskId ?? ''}|${String(d.date).slice(0, 10)}`;

// Les champs non datés (verrou, exemptions, droits) sont ceux de la réponse la
// plus récente ; les listes datées sont réunies.
function mergeCalendarData(prev, next) {
  return {
    ...prev,
    ...next,
    persons: mergePersons(prev.persons, next.persons),
    allocations: mergeByKey(prev.allocations, next.allocations, instanceKey),
    loggedTimes: mergeByKey(prev.loggedTimes, next.loggedTimes, (lt) => lt.id),
    timeOffs: mergeByKey(prev.timeOffs, next.timeOffs, instanceKey),
    holidaysByPerson: mergeHolidays(prev.holidaysByPerson, next.holidaysByPerson),
    dismissedSquares: mergeByKey(prev.dismissedSquares, next.dismissedSquares, dismissedKey),
  };
}

function pruneCalendarData(d, from, to) {
  return {
    ...d,
    allocations: (d.allocations ?? []).filter((a) => spanInRange(a, from, to)),
    loggedTimes: (d.loggedTimes ?? []).filter((lt) => dateInRange(lt, from, to)),
    timeOffs: (d.timeOffs ?? []).filter((t) => spanInRange(t, from, to)),
    holidaysByPerson: pruneHolidays(d.holidaysByPerson, from, to),
    dismissedSquares: (d.dismissedSquares ?? []).filter((x) => dateInRange(x, from, to)),
  };
}

// ---------- Géométrie (calquée sur ScheduleView) ----------
// Une seule densité (50 px/jour) pour rester lisible et cliquable au jour près.
//  - Semaine = 2 semaines consécutives (14 jours).
//  - Mois    = ~5 semaines (35 jours) — l'ancienne vision « Jour ».
const DAY_WIDTH_PX = 50;
// `visibleDays` = ce qu'on voit d'un coup d'oeil (dimensionne la largeur d'un
// jour, fit-to-width, et la taille d'un glissement de fenêtre). Ticket #91 : la
// fenêtre CHARGÉE est celle du Planning (cinq écrans, glissement à l'approche
// d'un bord, cf. useSlidingTimeline) — la molette défile en continu dans le
// temps au lieu de buter sur le bord de la fenêtre.
const PERIOD_BY_ZOOM = {
  week: { visibleDays: 14, shiftDays: 7, minDayWidth: DAY_WIDTH_PX, renderChunkDays: 7 },
  month: { visibleDays: 35, shiftDays: 14, minDayWidth: 30, renderChunkDays: 7 },
};
const ROW_HEIGHT_PX = 80;
// Capacité de référence d'une journée : 8h = hauteur utile de la cellule.
// Les squares sont dimensionnés proportionnellement à leurs heures ; au-delà
// de 8h cumulées sur un jour, l'empilement DÉBORDE la ligne (dépassement
// graphique proportionnel à la sur-saisie).
const CAPACITY_HOURS = 8;
const LEFT_COL_WIDTH_PX = 220;
const HEADER_HEIGHT_PX = 56;
// Bande blanche réservée EN BAS de chaque cellule-jour : permet toujours de
// créer une imputation sur la journée, même quand des allocations remplissent
// déjà la cellule (les squares restent calés en haut, cette bande reste libre).
const BLANK_CREATE_STRIP_PX = 18;
// Hauteur minimale lisible d'un square (assez pour afficher « CH202… 01:00 »).
// En dessous, une saisie 1h serait écrasée : la ligne s'agrandit alors pour
// caser N squares (ex. 8×1h) au lieu de comprimer chacun de façon illisible.
const MIN_SQUARE_PX = 22;
const SQUARE_GAP_PX = 2;
const BUFFER_DAYS = 3;
const BUFFER_ROWS = 3;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// ---------- Helpers dates ----------
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function parseISODate(s) {
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}
function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
function diffDays(a, b) {
  return Math.round((a.getTime() - b.getTime()) / MS_PER_DAY);
}
function startOfWeek(d) {
  const day = (d.getDay() + 6) % 7; // 0=lundi
  return addDays(d, -day);
}
function isWeekend(d) {
  const day = d.getDay();
  return day === 0 || day === 6;
}

const MONTHS_FR = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
];
const DAYS_FR_SHORT = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const DAYS_FR_LONG = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];

function fmtDate(d) {
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()]}`;
}
// Ticket #87 — libellé de la période réellement VISIBLE (pas la fenêtre
// chargée, trois fois plus large). Mois et année ne sont pas répétés quand
// les deux bornes les partagent (même format que le Planning, ticket #50).
function fmtPeriodTitle(from, to) {
  const sameYear = from.getFullYear() === to.getFullYear();
  if (sameYear && from.getMonth() === to.getMonth()) {
    return `${from.getDate()} → ${to.getDate()} ${MONTHS_FR[to.getMonth()]} ${to.getFullYear()}`;
  }
  const left = sameYear ? fmtDate(from) : `${fmtDate(from)} ${from.getFullYear()}`;
  return `${left} → ${fmtDate(to)} ${to.getFullYear()}`;
}

function displayPersonName(person) {
  if (!person) return '—';
  const first = person.firstName ?? '';
  const last = (person.lastName ?? '').toUpperCase();
  return `${first} ${last}`.trim();
}

// ---------- Couleurs ----------
const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);

/** Affaire `planningOnly` (proposition Synosight non gagnée) : planifiable,
 *  mais aucune imputation de temps possible. */
const PLANNING_ONLY_MSG = 'Affaire en planification uniquement : imputations fermées';

function affaireColor(a) {
  const raw = a?.color || a?.client?.color;
  if (!raw) return 'var(--accent)';
  return raw.startsWith('#') ? raw : `#${raw}`;
}
/** Convertit une couleur (hex ou var) en rgba translucide. Fallback --glass. */
function translucent(color, alpha) {
  if (typeof color !== 'string' || !color.startsWith('#')) return 'var(--glass)';
  let hex = color.slice(1);
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  if (hex.length !== 6) return 'var(--glass)';
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Ticket #20 — couleur de texte lisible sur un fond de couleur projet. Le blanc
 * codé en dur devenait illisible sur les couleurs claires (gris, pastel).
 * Retourne null si la couleur n'est pas parsable (variable CSS) → on garde le
 * comportement par défaut de l'appelant.
 */
function readableTextOn(bg) {
  if (typeof bg !== 'string' || !bg.startsWith('#')) return null;
  let hex = bg.slice(1);
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
  if (hex.length !== 6) return null;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.18 ? '#111827' : '#fff';
}

/**
 * Masque des jours ouvrés (depuis workDaysHours Float). Index par
 * `Date.getDay()` (0=dim … 6=sam). Fallback lun-ven.
 */
function getWorkingDayMask(workDaysHours) {
  const DEFAULT_5J = [false, true, true, true, true, true, false];
  if (!workDaysHours || typeof workDaysHours !== 'object') return DEFAULT_5J;
  if (Array.isArray(workDaysHours)) {
    if (workDaysHours.length !== 7) return DEFAULT_5J;
    return workDaysHours.map((v) => Number.isFinite(Number(v)) && Number(v) > 0);
  }
  const keys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return keys.map((k) => {
    const v = Number(workDaysHours[k]);
    return Number.isFinite(v) && v > 0;
  });
}

// ---------- Curseurs custom par mode (data-URI SVG) ----------
// Construits par `buildModeCursor` (components/ToolModeMenu.jsx, partagé avec
// le Planning — ticket #62).
const CURSOR_BY_MODE = {
  validate: buildModeCursor(`<path d='M19.5 23 l2.5 2.5 l4.5 -5'/>`, '#16a34a'),
  delete: buildModeCursor(`<path d='M20 20 l6 6 M26 20 l-6 6'/>`, '#dc2626'),
  edit: buildModeCursor(`<path d='M20.5 25.5 l3.8 -3.8 l1.5 1.5 l-3.8 3.8 z'/>`, '#326496'),
};
// Mode « Modification / Création » : le curseur dépend de l'état de la case —
// badge « + » (création) quand aucun log n'est présent, crayon (édition) sinon.
const CURSOR_EDIT_CREATE = buildModeCursor(`<path d='M23 18.5 v9 M18.5 23 h9'/>`, '#326496');

const pillBtn = {
  borderRadius: 999,
  border: '1px solid var(--glass-border)',
  padding: '4px 12px',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
};
const navBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-pill)',
  cursor: 'pointer',
  flexShrink: 0,
};

const MODES = [
  { key: 'validate', label: 'Validation', Icon: Check, color: 'var(--success)' },
  // Ticket #23 — le mode « Dévalidation » a été retiré (décision produit
  // 2026-07 : ne garder que « Suppression »). Suppression archive la saisie et
  // masque le square prévisionnel.
  { key: 'delete', label: 'Suppression', Icon: Trash2, color: 'var(--danger)' },
  { key: 'edit', label: 'Modification / Création', Icon: Pencil, color: 'var(--accent)' },
];

// Ticket #80 — nombre de lignes du champ Notes : 3 minimum, grandit avec le
// texte (≈ 60 caractères par ligne dans la modale), plafonné à 12 (au-delà, le
// champ défile).
function notesRows(text) {
  if (!text) return 3;
  const lines = text
    .split('\n')
    .reduce((n, line) => n + Math.max(1, Math.ceil(line.length / 60)), 0);
  return Math.min(12, Math.max(3, lines));
}

export default function LogtimeCalendarView({ user, viewMode, onChangeViewMode, initialDate }) {
  // Ticket support #15 — ouverture sur la semaine du jour ciblé (`&date=` d'un
  // lien profond, ou jour cliqué #49), sinon la semaine courante.
  const openAnchor = useMemo(() => {
    const base = initialDate ? new Date(`${initialDate}T00:00:00`) : TODAY;
    return startOfWeek(Number.isNaN(base.getTime()) ? TODAY : base);
  }, [initialDate]);
  const isAdmin = user?.role === 'ADMIN';
  const isManager = user?.role === 'MANAGER';

  const [zoom, setZoom] = useState('week');
  // Ticket #59 — mode par défaut « Modification / Création » et non
  // « Validation ». Ouvrir le détail d'une imputation est le geste le plus
  // courant à l'arrivée sur la vue, or il est réservé au mode `edit` (cf. le
  // garde `mode !== 'edit'` sur l'ouverture de la modale plus bas) : démarrer
  // en `validate` obligeait à un changement de mode par clic droit avant le
  // moindre clic utile. La validation reste accessible par le menu contextuel.
  const [mode, setMode] = useState('edit');
  // Densité d'affichage (hauteur des lignes) — partagée avec le Schedule.
  const [density, setDensity] = useState(() => readDensity());
  const rowHeight = DENSITY_ROW_HEIGHT[density] ?? ROW_HEIGHT_PX;
  const changeDensity = useCallback((d) => {
    setDensity(d);
    writeDensity(d);
  }, []);
  // Périmètre affiché : par défaut `self` (uniquement mes temps). MANAGER peut
  // passer à `team` (ses consultants) ; `all` (tout le monde) est ouvert à tous
  // les rôles en lecture seule depuis le ticket #45.
  const [scope, setScope] = useState('self');
  // Filtre facetté PARTAGÉ avec le Schedule (même scope `schedule`) → cohérence
  // des filtres entre les vues planning.
  const { conditions: filterConditions } = useFilterStore('schedule');
  // Un filtre autre que « Moi » force « Tout le monde » (à chaque changement
  // de filtre ; l'utilisateur peut ensuite rebasculer à la main).
  const nonSelfFilter = hasNonSelfFilter(filterConditions, user?.personId);
  const filterKey = JSON.stringify(toFilterQuery(filterConditions));
  useEffect(() => {
    if (nonSelfFilter) setScope('all');
  }, [nonSelfFilter, filterKey]);
  // Menu contextuel (clic droit) pour changer de mode rapidement.
  const [contextMenu, setContextMenu] = useState(null); // { x, y } | null
  const [reloadKey, setReloadKey] = useState(0);
  // Édition Person depuis le clic sur le nom dans la colonne de gauche (ADMIN).
  const [editPersonId, setEditPersonId] = useState(null);
  const [busyKeys, setBusyKeys] = useState({});
  const [editTarget, setEditTarget] = useState(null);
  const [toast, setToast] = useState(null);
  // Drag-to-move d'un square validé vers un autre jour (hors WE / temps partiel
  // / férié / verrou). `dragMove` = feedback visuel ; `dragMoveRef` = état live.
  const [dragMove, setDragMove] = useState(null);
  const dragMoveRef = useRef(null);
  const justDraggedRef = useRef(false);

  // Durée minimale de saisie (paramètre admin, défaut 1h).
  const [minHours, setMinHours] = useState(1);
  // Ticket #48 — ajustement vertical des heures d'une saisie existante.
  // hoursDrag = null OU { key, ltId, personId, iso, dayIndex, originalHours,
  //                       currentHours, startY, pxPerHour, step }
  const [hoursDrag, setHoursDrag] = useState(null);
  const hoursDragRef = useRef(null);
  useEffect(() => {
    apiSettings
      .get()
      .then((s) => setMinHours(Number(s?.logtimeMinHours ?? 1)))
      .catch(() => {});
  }, []);

  // ---- Défilement temporel (ticket #91) ----
  // Hook commun avec le Planning : fenêtre glissante, animation des flèches,
  // défilement continu touche ← / → maintenue. Pas de glissement pendant un
  // geste (déplacement d'une saisie, ajustement des heures).
  const busyRef = useRef(false);
  busyRef.current = Boolean(dragMove || hoursDrag);
  const zoomCfg = PERIOD_BY_ZOOM[zoom];
  const {
    containerRef,
    period,
    totalDays,
    dayWidth,
    viewport,
    scrollLeft,
    scrollTop,
    navigate: shift,
    anchorTo,
    containerProps,
  } = useSlidingTimeline({
    zoom,
    visibleDays: zoomCfg.visibleDays,
    shiftDays: zoomCfg.shiftDays,
    // Fit-to-width sur les jours VISIBLES, avec un plancher par zoom pour
    // rester lisible sur petit écran.
    computeDayWidth: (available) =>
      Math.max(zoomCfg.minDayWidth, Math.floor(available / Math.max(1, zoomCfg.visibleDays))),
    leftColWidth: LEFT_COL_WIDTH_PX,
    initialAnchor: openAnchor,
    // Au changement de zoom, la même semaine reste en haut à gauche.
    snapZoomAnchor: startOfWeek,
    busyRef,
    onScroll: () => setContextMenu(null),
  });
  const todayIndex = useMemo(() => diffDays(TODAY, period.from), [period.from]);

  const showToast = useCallback((msg, kind = 'error') => {
    setToast({ msg, kind });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  // ---- Chargement données (ticket #91 : par segments, sans overlay) ----
  // Seuls un changement de périmètre ou de filtre affichent « Chargement… » ;
  // un déplacement temporel ne demande que les jours nouveaux, en silence.
  const filterQuery = useMemo(() => toFilterQuery(filterConditions), [filterConditions]);
  const {
    data: schedule,
    setData: setSchedule,
    loading,
    fetching,
    error,
  } = useRangeData({
    period,
    depsKey: JSON.stringify({ scope, filter: filterQuery }),
    reloadKey,
    fetchRange: ({ from, to, signal }) =>
      // Ticket #100 — les saisies hors filtre reviennent aussi (grisées).
      apiLogtime.calendar({ from, to, withHolidays: true, withOutOfFilter: true, scope, filter: filterQuery, signal }),
    merge: mergeCalendarData,
    prune: pruneCalendarData,
    empty: EMPTY_CALENDAR,
  });

  // ---- Menu contextuel (clic droit) : changer de mode ----
  const openContextMenu = useCallback((e) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);
  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  // ---- Navigation ----
  // Flèches et clavier ← / → : `shift` (useSlidingTimeline). « Aujourd'hui »
  // ramène la semaine courante en haut à gauche, en défilant si elle est déjà
  // chargée.
  const gotoToday = useCallback(() => anchorTo(startOfWeek(TODAY)), [anchorTo]);

  const persons = schedule.persons ?? EMPTY_LIST;
  const myPersonId = schedule.myPersonId ?? null;
  // Un non-ADMIN ne peut saisir que sur sa propre Person (cohérent IDOR backend)
  // — plus, depuis le ticket #29, sur celle de ses subordonnés directs. La liste
  // `manageablePersonIds` est calculée SERVEUR (autorité) et reflète exactement
  // ce que le CRUD /api/logged-time accepte (`canManagePerson`).
  const manageablePersonIds = useMemo(
    () => new Set(schedule.manageablePersonIds ?? []),
    [schedule.manageablePersonIds],
  );
  // Ticket #29 (3e passe) — affaires dont je suis le gestionnaire : le CRUD
  // serveur (`isAffaireManager`) m'autorise à éditer les saisies de n'importe
  // qui sur CES affaires. Le calendrier doit refléter ce droit (avant, ces
  // cellules étaient affichées en lecture seule, avec un bandeau « période
  // verrouillée » trompeur).
  const managedAffaireIds = useMemo(
    () => new Set(schedule.managedAffaireIds ?? []),
    [schedule.managedAffaireIds],
  );
  const canMutateSquare = useCallback(
    (sq) =>
      isAdmin ||
      sq.personId === myPersonId ||
      manageablePersonIds.has(sq.personId) ||
      // Case vide (pas d'affaire connue) : un gestionnaire d'affaire peut créer
      // pour un tiers, la modale ne lui proposera que SES affaires.
      (sq.affaireId ? managedAffaireIds.has(sq.affaireId) : managedAffaireIds.size > 0),
    [isAdmin, myPersonId, manageablePersonIds, managedAffaireIds],
  );

  // ---- Index LoggedTime par cellule ----
  const lockMs = useMemo(() => {
    const lockDate = schedule.lock?.lockDate;
    return lockDate ? parseISODate(lockDate).getTime() : null;
  }, [schedule.lock]);
  // Sprint 20 — exemptions temporaires (déverrouillage). `personId=null` = global.
  // Une date <= cutoff redevient éditable si un grant applicable la couvre.
  const unlockGrants = schedule.unlockGrants ?? EMPTY_LIST;
  const isDateLocked = useCallback(
    (iso, personId = null) => {
      if (lockMs == null) return false;
      const dMs = parseISODate(iso).getTime();
      if (dMs > lockMs) return false;
      const granted = unlockGrants.some((g) => {
        if (g.personId != null && g.personId !== personId) return false;
        return dMs >= parseISODate(g.fromDate).getTime() && dMs <= parseISODate(g.toDate).getTime();
      });
      return !granted;
    },
    [lockMs, unlockGrants],
  );

  // Une même cellule (personne, affaire, phase, jour) peut porter PLUSIEURS
  // saisies — Float en crée couramment deux le même jour sur la même affaire
  // (ex. 2 h puis 6 h). Ces index conservent donc des LISTES : n'en garder
  // qu'une faisait afficher la même saisie sur plusieurs cases, et le total du
  // jour partait en dépassement fantôme (6 h + 6 h au lieu de 2 h + 6 h).
  const { ltByCell, ltById } = useMemo(() => {
    const byCell = new Map();
    const byId = new Map();
    const push = (m, k, v) => {
      const arr = m.get(k);
      if (arr) arr.push(v);
      else m.set(k, [v]);
    };
    for (const lt of schedule.loggedTimes ?? []) {
      const iso = String(lt.date).slice(0, 10);
      push(byCell, `${lt.personId}|${lt.affaireId}|${lt.phaseId ?? ''}|${iso}`, lt);
      byId.set(lt.id, lt);
    }
    return { ltByCell: byCell, ltById: byId };
  }, [schedule.loggedTimes]);

  // Chaque case porte l'identifiant de SA saisie, attribué à la construction :
  // une saisie n'apparaît ainsi qu'une fois, et une case ne peut pas emprunter
  // les heures d'une autre.
  const findLoggedTime = useCallback(
    (sq) => (sq.loggedTimeId ? (ltById.get(sq.loggedTimeId) ?? null) : null),
    [ltById],
  );

  // ---- Construit les squares (1 par jour ouvré) par personne ----
  const workingMaskByPerson = useMemo(() => {
    const m = new Map();
    for (const p of persons) m.set(p.id, getWorkingDayMask(p.workDaysHours));
    return m;
  }, [persons]);

  // Map<personId, Map<iso, holiday>> — Map interne pour récupérer name/regionCode
  // (hachure + tooltip). `.has(iso)` reste utilisable pour le skip des squares.
  const holidaySetByPerson = useMemo(() => {
    const m = new Map();
    const hbp = schedule.holidaysByPerson ?? {};
    for (const pid of Object.keys(hbp)) {
      const inner = new Map();
      for (const h of hbp[pid] ?? []) inner.set(String(h.date).slice(0, 10), h);
      m.set(pid, inner);
    }
    return m;
  }, [schedule.holidaysByPerson]);

  // Map<personId, Map<iso, timeOffInstance[]>> — TOUS les congés non refusés
  // (approuvés ET en attente, journée entière ET partiels). Sert à :
  // (1) afficher un bloc « congé » dans le calendrier (cohérence avec le
  // Schedule, qui montre également les demandes en attente), (2) masquer le
  // square d'allocation **uniquement** pour les congés APPROUVÉS journée
  // entière (la personne est alors totalement absente). Un congé partiel — ou
  // une demande encore en attente — laisse le square d'allocation visible,
  // empilé avec le bloc congé.
  const timeOffByPerson = useMemo(() => {
    const m = new Map();
    for (const inst of schedule.timeOffs ?? []) {
      if (inst.status === 'REJECTED') continue;
      const start = parseISODate(inst.startDate);
      const end = parseISODate(inst.endDate);
      const spanDays = Math.max(0, diffDays(end, start)) + 1;
      for (let i = 0; i < spanDays && i < 366; i++) {
        const day = addDays(start, i);
        const iso = toISODate(day);
        for (const pid of inst.personIds ?? []) {
          // Comme au Planning (splitAllocationIntoSegments) : un congé ne
          // s'affiche pas sur un jour non travaillé (week-end, temps partiel)
          // ni sur un férié de la personne.
          const mask = workingMaskByPerson.get(pid) ?? [false, true, true, true, true, true, false];
          if (!mask[day.getDay()]) continue;
          if (holidaySetByPerson.get(pid)?.has(iso)) continue;
          let inner = m.get(pid);
          if (!inner) {
            inner = new Map();
            m.set(pid, inner);
          }
          const arr = inner.get(iso);
          if (arr) arr.push(inst);
          else inner.set(iso, [inst]);
        }
      }
    }
    return m;
  }, [schedule.timeOffs, workingMaskByPerson, holidaySetByPerson]);

  // Un jour est « journée entière en congé » si au moins un congé APPROUVÉ
  // fullDay le couvre (une demande en attente ne bloque rien).
  const hasFullDayTimeOff = useCallback(
    (personId, iso) => {
      const inner = timeOffByPerson.get(personId);
      if (!inner) return false;
      const arr = inner.get(iso);
      return Boolean(arr && arr.some((t) => t.fullDay !== false && t.status === 'APPROVED'));
    },
    [timeOffByPerson],
  );

  // Allocations « remplacées » : un LoggedTime rattaché à l'allocation mais
  // saisi sur une AUTRE affaire. Le square planifié d'origine doit alors
  // disparaître (l'utilisateur a explicitement remplacé l'affaire prévue).
  const loggedAffairesByAllocation = useMemo(() => {
    const m = new Map(); // `${allocationId}|${iso}` -> Set<affaireId loggués>
    for (const lt of schedule.loggedTimes ?? []) {
      if (!lt.allocationId) continue;
      const iso = String(lt.date).slice(0, 10);
      const k = `${lt.allocationId}|${iso}`;
      let s = m.get(k);
      if (!s) {
        s = new Set();
        m.set(k, s);
      }
      s.add(lt.affaireId);
    }
    return m;
  }, [schedule.loggedTimes]);

  // Ticket #23 — clés des squares prévisionnels « supprimés » de la saisie.
  // Clé `${personId}|${affaireId}|${phaseId ?? ''}|${taskId ?? ''}|${iso}`
  // (alignée sur sq.key). Ticket #76 — la tâche en fait partie : sans elle,
  // écarter le square d'une tâche masquerait toutes les tâches de la même
  // affaire ce jour-là.
  const dismissedSet = useMemo(() => {
    const s = new Set();
    for (const d of schedule.dismissedSquares ?? []) {
      const iso = String(d.date).slice(0, 10);
      s.add(`${d.personId}|${d.affaireId}|${d.phaseId ?? ''}|${d.taskId ?? ''}|${iso}`);
    }
    return s;
  }, [schedule.dismissedSquares]);

  const squaresByPerson = useMemo(() => {
    const map = new Map(); // personId -> Map<dayIndex, square[]>
    for (const inst of schedule.allocations ?? []) {
      const mask = workingMaskByPerson.get(inst.personId) ?? [false, true, true, true, true, true, false];
      const holidays = holidaySetByPerson.get(inst.personId) ?? null;
      const timeOffs = timeOffByPerson.get(inst.personId) ?? null;
      const start = parseISODate(inst.startDate);
      const end = parseISODate(inst.endDate);
      const spanDays = Math.max(0, diffDays(end, start)) + 1;
      for (let i = 0; i < spanDays && i < 366; i++) {
        const cur = addDays(start, i);
        const dayIndex = diffDays(cur, period.from);
        if (dayIndex < 0 || dayIndex >= totalDays) continue;
        if (!mask[cur.getDay()]) continue;
        const iso = toISODate(cur);
        if (holidays && holidays.has(iso)) continue;
        // Jour de congé APPROUVÉ journée entière → pas de square d'allocation
        // (personne totalement absente). Un congé partiel — ou une demande
        // encore en attente — laisse passer le square.
        const dayTimeOffs = timeOffs ? timeOffs.get(iso) : null;
        if (
          dayTimeOffs &&
          dayTimeOffs.some((t) => t.fullDay !== false && t.status === 'APPROVED')
        ) {
          continue;
        }
        // Allocation remplacée ce jour-là par un log sur une autre affaire :
        // on masque le square planifié (sauf si l'affaire prévue a aussi été
        // loguée, auquel cas le square validé reste affiché).
        const loggedAff = loggedAffairesByAllocation.get(`${inst.id}|${iso}`);
        if (loggedAff && loggedAff.size > 0 && !loggedAff.has(inst.affaireId)) continue;
        // Ticket #23 — square prévisionnel « supprimé » de la saisie : on ne le
        // regénère pas (le scheduling reste intact, c'est juste masqué ici).
        if (
          dismissedSet.has(
            `${inst.personId}|${inst.affaireId}|${inst.phaseId ?? ''}|${inst.taskId ?? ''}|${iso}`,
          )
        ) {
          continue;
        }
        const sq = {
          key: `${inst.personId}|${inst.affaireId}|${inst.phaseId ?? ''}|${inst.taskId ?? ''}|${iso}`,
          personId: inst.personId,
          affaireId: inst.affaireId,
          affaire: inst.affaire,
          phaseId: inst.phaseId ?? null,
          phase: inst.phase ?? null,
          taskId: inst.taskId ?? null,
          task: inst.task ?? null,
          allocationId: inst.id,
          hoursPerDay: Number(inst.hoursPerDay) || 0,
          billable: inst.billable !== false,
          name: inst.name ?? null,
          notes: inst.notes ?? null,
          iso,
          dayIndex,
        };
        let byDay = map.get(inst.personId);
        if (!byDay) {
          byDay = new Map();
          map.set(inst.personId, byDay);
        }
        const arr = byDay.get(dayIndex);
        if (arr) arr.push(sq);
        else byDay.set(dayIndex, [sq]);
      }
    }

    // ---- Attribution des saisies aux cases planifiées ----
    //
    // Chaque saisie doit apparaître UNE fois et une seule. On attribue d'abord
    // les saisies aux cases d'allocation qui leur correspondent (même affaire,
    // même phase de préférence) ; ce qui reste sans case en obtiendra une
    // ci-dessous.
    //
    // Une case d'allocation ne prend qu'une saisie : deux saisies sur la même
    // cellule donnent donc une case validée + une case « log-only », et non
    // deux cases affichant la même saisie.
    const restantes = new Map(); // clé cellule -> saisies pas encore attribuées
    // Ticket #91 — mêmes listes indexées sans la phase, dans l'ordre de
    // `restantes`, pour le repli ci-dessous. Il parcourait toutes les cellules
    // pour chaque case planifiée : quadratique, soit plusieurs secondes de
    // calcul par segment chargé en zoom Mois sur « Tout le monde ».
    const restantesSansPhase = new Map(); // `${personne}|${affaire}|${jour}` -> listes
    // Ticket #100 — les saisies hors du filtre actif ne prennent jamais une case
    // planifiée : elles ont chacune leur case grisée, en lecture seule.
    const horsFiltre = [];
    for (const [k, arr] of ltByCell) {
      const copie = arr.filter((lt) => !lt.outOfFilter);
      for (const lt of arr) if (lt.outOfFilter) horsFiltre.push(lt);
      restantes.set(k, copie);
      const [p, a, , d] = k.split('|');
      const k2 = `${p}|${a}|${d}`;
      const listes = restantesSansPhase.get(k2);
      if (listes) listes.push(copie);
      else restantesSansPhase.set(k2, [copie]);
    }
    const prendre = (personId, affaireId, phaseId, iso) => {
      const exact = restantes.get(`${personId}|${affaireId}|${phaseId ?? ''}|${iso}`);
      if (exact && exact.length > 0) return exact.shift();
      // Repli : même affaire et même jour, phase différente — la saisie reste
      // celle de cette imputation, la phase n'est qu'un détail de rattachement.
      for (const arr of restantesSansPhase.get(`${personId}|${affaireId}|${iso}`) ?? []) {
        if (arr.length > 0) return arr.shift();
      }
      return null;
    };

    for (const [, byDay] of map) {
      for (const [, squares] of byDay) {
        for (const sq of squares) {
          if (sq.kind === 'timeoff') continue;
          const lt = prendre(sq.personId, sq.affaireId, sq.phaseId, sq.iso);
          if (lt) sq.loggedTimeId = lt.id;
        }
      }
    }

    // Saisies restées sans case : log libre (saisie manuelle, import Float,
    // jour non planifié), ou saisie supplémentaire sur une cellule déjà
    // pourvue. Sans cela elle serait invisible et donc non éditable.
    for (const arr of [...restantes.values(), horsFiltre]) {
      for (const lt of arr) {
        const iso = String(lt.date).slice(0, 10);
        const cur = parseISODate(iso);
        const dayIndex = diffDays(cur, period.from);
        if (dayIndex < 0 || dayIndex >= totalDays) continue;
        const sq = {
          // L'identifiant de la saisie entre dans la clé : deux saisies sur la
          // même cellule sont deux cases distinctes, pas deux fois la même.
          key: `${lt.personId}|${lt.affaireId}|${lt.phaseId ?? ''}|${lt.taskId ?? ''}|${iso}|${lt.id}`,
          personId: lt.personId,
          affaireId: lt.affaireId,
          affaire: lt.affaire ?? null,
          phaseId: lt.phaseId ?? null,
          phase: lt.phase ?? null,
          taskId: lt.taskId ?? null,
          task: lt.task ?? null,
          allocationId: lt.allocationId ?? null,
          loggedTimeId: lt.id,
          hoursPerDay: 0, // pas de prévision planning pour un log libre
          billable: lt.billable !== false,
          name: null,
          notes: lt.notes ?? null,
          iso,
          dayIndex,
          logOnly: true,
          outOfFilter: Boolean(lt.outOfFilter),
        };
        let byDay = map.get(lt.personId);
        if (!byDay) {
          byDay = new Map();
          map.set(lt.personId, byDay);
        }
        const dayArr = byDay.get(dayIndex);
        if (dayArr) dayArr.push(sq);
        else byDay.set(dayIndex, [sq]);
      }
    }

    // Blocs CONGÉ (approuvés ET en attente, entiers + partiels) — affichés dans
    // le calendrier pour refléter le Schedule. Non validables (info seule). Empilés en TÊTE
    // de la cellule (au-dessus des squares d'allocation).
    for (const [pid, byIso] of timeOffByPerson) {
      for (const [iso, insts] of byIso) {
        const cur = parseISODate(iso);
        const dayIndex = diffDays(cur, period.from);
        if (dayIndex < 0 || dayIndex >= totalDays) continue;
        for (const inst of insts) {
          const sq = {
            key: `timeoff|${pid}|${inst.id}|${iso}`,
            kind: 'timeoff',
            personId: pid,
            iso,
            dayIndex,
            fullDay: inst.fullDay !== false,
            // #64 — part de CE jour réellement couverte : une absence bornée à
            // la demi-journée n'occupe que la moitié de la fenêtre sur son jour
            // de bord, et non la journée entière.
            coverage: dayCoverageFactor(inst, iso),
            status: inst.status ?? 'APPROVED',
            hoursPerDay: Number(inst.hoursPerDay) || 0,
            timeOffType: inst.timeOffType ?? null,
            name: inst.timeOffType?.name ?? 'Congé',
            notes: inst.notes ?? null,
          };
          let byDay = map.get(pid);
          if (!byDay) {
            byDay = new Map();
            map.set(pid, byDay);
          }
          const arr = byDay.get(dayIndex);
          if (arr) arr.unshift(sq);
          else byDay.set(dayIndex, [sq]);
        }
      }
    }

    // Une journée déjà validée à pleine capacité (≥ 8h saisies) ne doit plus
    // afficher les squares planifiés NON validés (le prévisionnel restant est
    // sans objet puisque la journée est complète). On retire donc, par jour,
    // les squares d'allocation sans LoggedTime dès que le cumul des heures
    // validées du jour atteint la capacité de référence. Les blocs congé, les
    // log-only et les squares validés restent affichés.
    for (const [, byDay] of map) {
      for (const [dayIndex, squares] of byDay) {
        let validatedHours = 0;
        for (const s of squares) {
          if (s.kind === 'timeoff') continue;
          const lt = findLoggedTime(s);
          if (lt) validatedHours += Number(lt.hours) || 0;
        }
        if (validatedHours >= CAPACITY_HOURS) {
          const kept = squares.filter((s) => {
            if (s.kind === 'timeoff') return true;
            return Boolean(findLoggedTime(s)); // garde uniquement les jours validés
          });
          if (kept.length !== squares.length) byDay.set(dayIndex, kept);
        }
      }
    }

    return map;
  }, [schedule.allocations, ltByCell, dismissedSet, loggedAffairesByAllocation, workingMaskByPerson, holidaySetByPerson, timeOffByPerson, findLoggedTime, period.from, totalDays]);

  // ---- Mutations état local (évite un reload complet) ----
  const addLoggedTime = useCallback((lt) => {
    const iso = String(lt.date).slice(0, 10);
    setSchedule((s) => ({
      ...s,
      loggedTimes: [...(s.loggedTimes ?? []), lt],
      // Ticket #23 — (re)saisir sur un jour lève le marqueur « supprimé »
      // (cohérent avec le nettoyage backend du POST /logged-time).
      dismissedSquares: (s.dismissedSquares ?? []).filter(
        (d) =>
          !(
            d.personId === lt.personId &&
            d.affaireId === lt.affaireId &&
            String(d.date).slice(0, 10) === iso
          ),
      ),
    }));
  }, []);
  // Ticket #23 — marque localement un square prévisionnel comme « supprimé ».
  const addDismissed = useCallback((sq) => {
    setSchedule((s) => ({
      ...s,
      dismissedSquares: [
        ...(s.dismissedSquares ?? []),
        {
          personId: sq.personId,
          affaireId: sq.affaireId,
          phaseId: sq.phaseId ?? null,
          taskId: sq.taskId ?? null,
          date: sq.iso,
        },
      ],
    }));
  }, []);
  const replaceLoggedTime = useCallback((lt) => {
    setSchedule((s) => ({
      ...s,
      loggedTimes: (s.loggedTimes ?? []).map((x) => (x.id === lt.id ? lt : x)),
    }));
  }, []);
  const removeLoggedTime = useCallback((id) => {
    setSchedule((s) => ({
      ...s,
      loggedTimes: (s.loggedTimes ?? []).filter((x) => x.id !== id),
    }));
  }, []);

  const setBusy = useCallback((key, v) => {
    setBusyKeys((s) => {
      if (v) return { ...s, [key]: true };
      const out = { ...s };
      delete out[key];
      return out;
    });
  }, []);

  // ---- Action au clic selon le mode ----
  const handleSquareClick = useCallback(
    async (sq) => {
      // Ticket #100 — saisie hors du filtre actif : affichée pour la lecture
      // du jour, pas pour être modifiée (retirer le filtre pour y accéder).
      if (sq.outOfFilter) {
        showToast('Saisie hors du filtre actif — retirez le filtre pour la modifier', 'info');
        return;
      }
      const lt = findLoggedTime(sq);
      const locked = isDateLocked(sq.iso, sq.personId);
      const readOnly = !canMutateSquare(sq);

      if (mode === 'edit') {
        // Ouvre la modale ; en lecture seule si ligne d'autrui (MANAGER) ou verrou.
        setEditTarget({ square: sq, loggedTime: lt, locked, readOnly });
        return;
      }
      if (readOnly) {
        showToast('Lecture seule — vous ne pouvez saisir que vos propres heures, celles de votre équipe ou de vos affaires');
        return;
      }
      if (locked) {
        showToast('Période verrouillée — saisie en lecture seule');
        return;
      }
      if (busyKeys[sq.key]) return;

      if (mode === 'validate') {
        // Affaire en planification uniquement : imputations fermées (le
        // backend refuse en 403, y compris pour un ADMIN).
        if (sq.affaire?.planningOnly) {
          showToast(PLANNING_ONLY_MSG);
          return;
        }
        // Déjà validé → no-op (ticket #13). Le mode Validation ne sert qu'à
        // valider un prévisionnel ; modifier une saisie déjà validée passe par
        // le mode « Modification ». On guide l'utilisateur au lieu d'écraser.
        if (lt) {
          showToast('Déjà validé — passez en mode « Modification » pour ajuster');
          return;
        }
        setBusy(sq.key, true);
        try {
          const created = await apiLoggedTime.create({
            personId: sq.personId,
            affaireId: sq.affaireId,
            date: sq.iso,
            hours: sq.hoursPerDay > 0 ? sq.hoursPerDay : 1,
            billable: sq.billable,
            source: 'planning',
            ...(sq.phaseId ? { phaseId: sq.phaseId } : {}),
            ...(sq.taskId ? { taskId: sq.taskId } : {}),
            ...(sq.allocationId ? { allocationId: sq.allocationId } : {}),
          });
          addLoggedTime(created);
        } catch (e) {
          showToast(e.message);
        } finally {
          setBusy(sq.key, false);
        }
        return;
      }

      // Suppression (ticket #23) : retire la saisie ET masque le square
      // prévisionnel dans la saisie de temps, **sans toucher au scheduling**
      // (l'allocation reste intacte dans le planning). Marche aussi sur un
      // prévisionnel non encore validé (pas de LoggedTime). Un square « log
      // libre » (sans allocation) n'a pas de prévisionnel à masquer → simple
      // suppression de la saisie.
      if (mode === 'delete') {
        const isPlanned = Boolean(sq.allocationId) && !sq.logOnly;
        if (!lt && !isPlanned) return; // rien à supprimer
        setBusy(sq.key, true);
        try {
          if (lt) {
            await apiLoggedTime.archive(lt.id);
            removeLoggedTime(lt.id);
          }
          if (isPlanned) {
            await apiLogtime.dismissSquare({
              personId: sq.personId,
              affaireId: sq.affaireId,
              phaseId: sq.phaseId ?? null,
              taskId: sq.taskId ?? null,
              date: sq.iso,
            });
            addDismissed(sq);
          }
        } catch (e) {
          showToast(e.message);
        } finally {
          setBusy(sq.key, false);
        }
      }
    },
    [mode, findLoggedTime, isDateLocked, canMutateSquare, busyKeys, setBusy, addLoggedTime, removeLoggedTime, addDismissed, showToast],
  );

  // ---- Drag-to-move : déplacer un square validé vers un autre jour ----
  // Cible valide = dans la fenêtre, jour ouvré de la personne (≠ WE / temps
  // partiel), non férié, non verrouillé.
  const isValidMoveTarget = useCallback(
    (personId, dayIndex) => {
      if (dayIndex < 0 || dayIndex >= totalDays) return false;
      const mask = workingMaskByPerson.get(personId) ?? [false, true, true, true, true, true, false];
      const d = addDays(period.from, dayIndex);
      if (!mask[d.getDay()]) return false; // WE + temps partiel
      const iso = toISODate(d);
      const hs = holidaySetByPerson.get(personId);
      if (hs && hs.has(iso)) return false; // férié
      if (isDateLocked(iso, personId)) return false; // verrou comptable
      return true;
    },
    [totalDays, workingMaskByPerson, holidaySetByPerson, period.from, isDateLocked],
  );

  const onSquarePointerDown = useCallback(
    (e, sq, lt) => {
      // Drag réservé aux squares VALIDÉS (un LoggedTime existe), éditables.
      if (!lt) return;
      if (e.button !== undefined && e.button !== 0) return;
      if (!canMutateSquare(sq)) return;
      if (sq.affaire?.planningOnly) return; // imputations fermées
      if (isDateLocked(sq.iso, sq.personId)) return;
      dragMoveRef.current = {
        key: sq.key,
        ltId: lt.id,
        personId: sq.personId,
        fromDayIndex: sq.dayIndex,
        startX: e.clientX,
        moved: false,
      };
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    },
    [canMutateSquare, isDateLocked],
  );

  const onSquarePointerMove = useCallback(
    (e) => {
      const drag = dragMoveRef.current;
      if (!drag) return;
      const deltaPx = e.clientX - drag.startX;
      if (!drag.moved && Math.abs(deltaPx) < 4) return; // seuil anti-clic
      drag.moved = true;
      const deltaDays = Math.round(deltaPx / dayWidth);
      const targetDayIndex = drag.fromDayIndex + deltaDays;
      const valid = targetDayIndex !== drag.fromDayIndex && isValidMoveTarget(drag.personId, targetDayIndex);
      setDragMove({
        key: drag.key,
        personId: drag.personId,
        fromDayIndex: drag.fromDayIndex,
        targetDayIndex,
        valid,
        deltaPx,
      });
    },
    [dayWidth, isValidMoveTarget],
  );

  const onSquarePointerUp = useCallback(
    async (e) => {
      const drag = dragMoveRef.current;
      dragMoveRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      setDragMove(null);
      if (!drag || !drag.moved) return;
      // Un drag a eu lieu → on consomme le onClick qui suit (anti-ouverture modale).
      justDraggedRef.current = true;
      const deltaDays = Math.round((e.clientX - drag.startX) / dayWidth);
      const targetDayIndex = drag.fromDayIndex + deltaDays;
      if (targetDayIndex === drag.fromDayIndex) return;
      if (!isValidMoveTarget(drag.personId, targetDayIndex)) {
        showToast('Déplacement impossible sur ce jour (week-end, temps partiel, férié ou verrou)');
        return;
      }
      const targetIso = toISODate(addDays(period.from, targetDayIndex));
      setBusy(drag.key, true);
      try {
        const updated = await apiLoggedTime.update(drag.ltId, { date: targetIso });
        replaceLoggedTime(updated);
      } catch (err) {
        showToast(err.message);
      } finally {
        setBusy(drag.key, false);
      }
    },
    [dayWidth, isValidMoveTarget, period.from, setBusy, replaceLoggedTime, showToast],
  );

  // ---- Clic sur une case VIDE (aucune allocation planifiée ce jour) ----
  // Uniquement en mode « Modification / Création » : ouvre la modale de
  // création avec sélecteur d'affaire (puisqu'aucune allocation source).
  const handleBlankCellClick = useCallback(
    (personId, iso, exceptionalReason = null) => {
      if (mode !== 'edit') return;
      // Un drag-to-move qui se termine au-dessus du fond d'une cellule émet un
      // click sur ce fond : on le consomme pour ne pas ouvrir la modale.
      if (justDraggedRef.current) {
        justDraggedRef.current = false;
        return;
      }
      if (isDateLocked(iso, personId)) {
        showToast('Période verrouillée — saisie impossible');
        return;
      }
      if (!canMutateSquare({ personId })) {
        showToast('Lecture seule — vous ne pouvez saisir que vos propres heures, celles de votre équipe ou de vos affaires');
        return;
      }
      // Saisie sur un jour non travaillé / férié / congé → confirmation.
      if (exceptionalReason) {
        const d = parseISODate(iso);
        const label = `${DAYS_FR_LONG[(d.getDay() + 6) % 7]} ${d.getDate()} ${MONTHS_FR[d.getMonth()]}`;
        if (!window.confirm(`Le ${label} est ${exceptionalReason}.\nConfirmer la saisie de temps sur ce jour ?`)) {
          return;
        }
      }
      setEditTarget({ blank: { personId, iso }, locked: false, readOnly: false });
    },
    [mode, isDateLocked, canMutateSquare, showToast],
  );

  // ---- Hauteurs de ligne variables ----
  // 1h = hauteur CONSTANTE (pxPerHour) sur toutes les lignes. La référence
  // densité (rowHeight) correspond à une journée de 8h. Si un jour d'un
  // collaborateur dépasse 8h cumulées, SA ligne s'agrandit (au lieu de laisser
  // déborder le square sur la ligne suivante).
  // Heures effectives d'un square (logged si validé, sinon prévu ; congé = quota).
  const effHoursOf = useCallback(
    (s) => {
      if (s.kind === 'timeoff') {
        // Une demi-journée occupe 4 h, pas 8 : `coverage` vaut 0.5 sur le jour
        // de bord d'une absence bornée (cf. dayCoverageFactor).
        return s.fullDay ? CAPACITY_HOURS * (s.coverage ?? 1) : Number(s.hoursPerDay) || 0;
      }
      const found = findLoggedTime(s);
      if (!found) return Number(s.hoursPerDay) || 0;
      // Ticket #48 — pendant le geste, la case (et sa ligne) suit les heures en cours.
      if (hoursDrag && hoursDrag.ltId === found.id) return hoursDrag.currentHours;
      return Number(found.hours) || 0;
    },
    [findLoggedTime, hoursDrag],
  );

  // Layout vertical des squares d'UN jour : chaque square fait
  // `max(MIN_SQUARE_PX, heures × pxPerHour)` (un 1h reste lisible), empilé par
  // hauteur RENDUE (pas par heures) — donc 8×1h prennent 8×MIN_SQUARE_PX et la
  // ligne s'agrandit en conséquence. Retourne aussi la position px de la ligne
  // des 8h (proportionnelle aux heures) pour le repère de dépassement.
  const layoutDaySquares = useCallback(
    (squares, pxPerHour) => {
      let cumPx = 0;
      let cumHours = 0;
      let capacityLinePx = null;
      const items = (squares ?? []).map((sq) => {
        const effHours = effHoursOf(sq);
        const slotPx = Math.max(MIN_SQUARE_PX, effHours * pxPerHour);
        const topPx = cumPx;
        if (capacityLinePx === null && cumHours + effHours >= CAPACITY_HOURS) {
          const frac = effHours > 0 ? (CAPACITY_HOURS - cumHours) / effHours : 0;
          capacityLinePx = topPx + Math.max(0, Math.min(1, frac)) * slotPx;
        }
        cumPx += slotPx;
        cumHours += effHours;
        return { sq, topPx, heightPx: slotPx, effHours };
      });
      return { items, totalPx: cumPx, totalHours: cumHours, capacityLinePx };
    },
    [effHoursOf],
  );

  const rowLayout = useMemo(() => {
    const innerBase = Math.max(1, rowHeight - 12); // marge haut/bas (6 + 6)
    const basePxPerHour = innerBase / CAPACITY_HOURS;
    // Borne anti-explosion : au-delà, un square de 0.5h atteint déjà le minimum.
    const MAX_PX_PER_HOUR = MIN_SQUARE_PX / 0.5;
    const pxPerHourByRow = [];
    const heights = persons.map((person) => {
      const byDay = squaresByPerson.get(person.id);
      // Échelle px/heure PROPRE à la ligne : on l'augmente juste assez pour que
      // le plus petit square (p.ex. 1h) atteigne MIN_SQUARE_PX. Tous les squares
      // restent alors strictement proportionnels → 8h occupe la même hauteur
      // quel que soit le découpage (8×1h == 6h+2h).
      let minEff = CAPACITY_HOURS;
      if (byDay) {
        for (const [, squares] of byDay) {
          for (const s of squares) {
            const h = effHoursOf(s);
            if (h > 0 && h < minEff) minEff = h;
          }
        }
      }
      const rowPxPerHour = Math.min(
        MAX_PX_PER_HOUR,
        Math.max(basePxPerHour, MIN_SQUARE_PX / Math.max(minEff, 0.0001)),
      );
      pxPerHourByRow.push(rowPxPerHour);
      let maxContentPx = innerBase; // plancher : jamais plus petit qu'une journée 8h (échelle base)
      if (byDay) {
        for (const [, squares] of byDay) {
          const { totalPx } = layoutDaySquares(squares, rowPxPerHour);
          if (totalPx > maxContentPx) maxContentPx = totalPx;
        }
      }
      // +12 = marges haut/bas (6+6), + bande blanche de création en bas.
      return Math.ceil(maxContentPx + 12) + BLANK_CREATE_STRIP_PX;
    });
    const tops = [];
    let acc = 0;
    for (const h of heights) {
      tops.push(acc);
      acc += h;
    }
    return { pxPerHourByRow, heights, tops, totalHeight: acc };
  }, [persons, squaresByPerson, layoutDaySquares, effHoursOf, rowHeight]);

  // ---- Ticket #48 — ajuster les heures d'une saisie à la souris ----
  // Poignée basse des cases VALIDÉES (une saisie existe) : tirer vers le bas
  // augmente, vers le haut diminue, au pas `logtimeMinHours`, jusqu'à 12h.
  // Au-delà de la capacité du jour, confirmation explicite (comme le
  // surbooking du planning). Droits, verrou (423) et affaires fermées restent
  // arbitrés par `PATCH /api/logged-time/:id`.
  const onHoursHandlePointerDown = useCallback(
    (e, sq, lt, pxPerHour) => {
      e.stopPropagation(); // pas de déplacement horizontal de la case
      if (e.button !== undefined && e.button !== 0) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const hours = Number(lt.hours) || 0;
      const drag = {
        key: sq.key,
        ltId: lt.id,
        personId: sq.personId,
        iso: sq.iso,
        dayIndex: sq.dayIndex,
        originalHours: hours,
        currentHours: hours,
        startY: e.clientY,
        pxPerHour: Math.max(1, pxPerHour),
        step: normalizeHourStep(minHours),
      };
      hoursDragRef.current = drag;
      setHoursDrag(drag);
    },
    [minHours],
  );

  const onHoursHandlePointerMove = useCallback((e) => {
    e.stopPropagation();
    const d = hoursDragRef.current;
    if (!d) return;
    const dy = e.clientY - d.startY;
    // Sans vrai geste, on garde la valeur d'origine (pas d'arrondi au clic).
    const next =
      Math.abs(dy) < 3
        ? d.originalHours
        : snapHours(d.originalHours + dy / d.pxPerHour, d.step, MAX_HOURS_PER_DAY);
    if (next === d.currentHours) return;
    const updated = { ...d, currentHours: next };
    hoursDragRef.current = updated;
    setHoursDrag(updated);
  }, []);

  const onHoursHandlePointerUp = useCallback(
    async (e) => {
      e.stopPropagation();
      const d = hoursDragRef.current;
      hoursDragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      setHoursDrag(null);
      if (!d || d.currentHours === d.originalHours) return;
      // Confirmation seulement quand on AUGMENTE au-delà de la capacité : le
      // total du jour = autres saisies de la personne + congés + nouvelle valeur.
      if (d.currentHours > d.originalHours) {
        const otherLogged = (schedule.loggedTimes ?? [])
          .filter(
            (x) =>
              x.id !== d.ltId &&
              x.personId === d.personId &&
              String(x.date).slice(0, 10) === d.iso,
          )
          .reduce((sum, x) => sum + (Number(x.hours) || 0), 0);
        const timeOffHours = (squaresByPerson.get(d.personId)?.get(d.dayIndex) ?? [])
          .filter((x) => x.kind === 'timeoff')
          .reduce((sum, x) => sum + effHoursOf(x), 0);
        const dayTotal = otherLogged + timeOffHours + d.currentHours;
        if (dayTotal > CAPACITY_HOURS) {
          const date = parseISODate(d.iso);
          const label = `${DAYS_FR_LONG[(date.getDay() + 6) % 7]} ${date.getDate()} ${MONTHS_FR[date.getMonth()]}`;
          const ok = window.confirm(
            `Le ${label}, le total passera à ${formatHHMM(dayTotal)}, ` +
              `au-delà des ${CAPACITY_HOURS}h de la journée.\nConfirmer la saisie ?`,
          );
          if (!ok) return;
        }
      }
      setBusy(d.key, true);
      try {
        const updated = await apiLoggedTime.update(d.ltId, { hours: d.currentHours });
        replaceLoggedTime(updated);
      } catch (err) {
        showToast(err.message);
      } finally {
        setBusy(d.key, false);
      }
    },
    [schedule.loggedTimes, squaresByPerson, effHoursOf, setBusy, replaceLoggedTime, showToast],
  );

  // ---- Virtualisation (hauteurs variables) ----
  const totalRows = persons.length;
  const visibleHeight = Math.max(0, viewport.height - HEADER_HEIGHT_PX);
  // Ticket #91 — recherche binaire (les `tops` sont croissants) au lieu d'un
  // parcours linéaire des lignes à chaque image de scroll.
  // Premier index dont le bas dépasse `y` / dont le haut atteint `y`.
  const lowerBound = (pred) => {
    let lo = 0;
    let hi = totalRows;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (pred(mid)) hi = mid;
      else lo = mid + 1;
    }
    return lo;
  };
  const firstVisibleRow = Math.max(
    0,
    lowerBound((i) => rowLayout.tops[i] + rowLayout.heights[i] >= scrollTop) - BUFFER_ROWS,
  );
  const lastVisibleRow = Math.min(
    totalRows,
    lowerBound((i) => rowLayout.tops[i] >= scrollTop + visibleHeight) + BUFFER_ROWS,
  );
  // Plage de jours RENDUE, arrondie par blocs (ticket #91) : elle ne change
  // qu'au franchissement d'un bloc, ce qui permet de mémoïser les lignes
  // (`rowsElements`) pendant le défilement.
  const visibleWidth = Math.max(0, viewport.width - LEFT_COL_WIDTH_PX);
  const renderChunk = zoomCfg.renderChunkDays;
  const firstVisibleDay = Math.max(
    0,
    Math.floor((Math.floor(scrollLeft / dayWidth) - BUFFER_DAYS) / renderChunk) * renderChunk,
  );
  const lastVisibleDay = Math.min(
    totalDays,
    Math.ceil((Math.ceil((scrollLeft + visibleWidth) / dayWidth) + BUFFER_DAYS) / renderChunk) *
      renderChunk,
  );
  // Ticket #87 — bornes réellement à l'écran (sans BUFFER_DAYS), pour le
  // libellé de période de la toolbar : il suit le défilement horizontal.
  const visibleFromIdx = Math.max(0, Math.min(totalDays - 1, Math.floor(scrollLeft / dayWidth)));
  const visibleToIdx = Math.max(
    visibleFromIdx,
    Math.min(totalDays - 1, Math.ceil((scrollLeft + visibleWidth) / dayWidth) - 1),
  );
  const visibleFrom = addDays(period.from, visibleFromIdx);
  const visibleTo = addDays(period.from, visibleToIdx);

  const modeCursor = CURSOR_BY_MODE[mode];


  // Ticket #91 — lignes de la grille mémoïsées : elles ne dépendent pas de la
  // position de défilement, seulement de la plage de lignes / de jours rendue
  // (arrondie par blocs) et des données. Sans cela, chaque image de
  // défilement re-rendait toutes les cases visibles (des milliers en zoom Mois
  // sur « Tout le monde »).
  const rowsElements = useMemo(
    () =>
      persons.slice(firstVisibleRow, lastVisibleRow).map((person, idx) => {
    const rowIndex = firstVisibleRow + idx;
    const top = rowLayout.tops[rowIndex];
    const personRowHeight = rowLayout.heights[rowIndex];
    const rowPxPerHour = rowLayout.pxPerHourByRow[rowIndex];
    const byDay = squaresByPerson.get(person.id);
    const personHolidays = holidaySetByPerson.get(person.id) ?? null;
    const personTimeOffs = timeOffByPerson.get(person.id) ?? null;
    const personMask = workingMaskByPerson.get(person.id) ?? [false, true, true, true, true, true, false];
    return (
      <div
        key={person.id}
        style={{
          position: 'absolute',
          top,
          left: 0,
          height: personRowHeight,
          width: LEFT_COL_WIDTH_PX + totalDays * dayWidth,
          borderBottom: '1px solid var(--glass-border)',
          display: 'flex',
        }}
      >
        {/* Col gauche */}
        <div
          style={{
            position: 'sticky',
            left: 0,
            zIndex: 8,
            width: LEFT_COL_WIDTH_PX,
            flex: '0 0 auto',
            background: 'var(--card-bg)',
            borderRight: '1px solid var(--glass-border)',
            padding: '8px 12px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            boxSizing: 'border-box',
            cursor: 'default',
          }}
        >
          {isAdmin ? (
            <button
              type="button"
              onClick={() => setEditPersonId(person.id)}
              title={`Modifier ${displayPersonName(person)}`}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                margin: 0,
                font: 'inherit',
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--accent)',
                cursor: 'pointer',
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {displayPersonName(person)}
            </button>
          ) : (
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={displayPersonName(person)}
            >
              {displayPersonName(person)}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {person.role?.name ?? person.jobTitle ?? '—'}
          </div>
        </div>

        {/* Zone jours */}
        <div style={{ position: 'relative', flex: '1 0 auto', width: totalDays * dayWidth, height: personRowHeight }}>
          {/* Fond cellules visibles (weekend / today / férié hachuré) */}
          {Array.from({ length: Math.max(0, lastVisibleDay - firstVisibleDay) }).map((_, i) => {
            const dayIndex = firstVisibleDay + i;
            const d = addDays(period.from, dayIndex);
            const isToday = dayIndex === todayIndex;
            const weekend = isWeekend(d);
            const iso = toISODate(d);
            const holiday = personHolidays ? personHolidays.get(iso) : null;
            const isHoliday = Boolean(holiday);
            const dayTimeOffs = personTimeOffs ? personTimeOffs.get(iso) : null;
            const isTimeOff = Boolean(dayTimeOffs && dayTimeOffs.length);
            const isWorkingDay = personMask[d.getDay()];
            // Jour temps partiel = jour non ouvré pour CETTE personne qui
            // n'est pas un week-end (p.ex. mercredi à 0h). Grisé + hachuré
            // (cohérent avec le Schedule).
            const isPartTimeDay = !isWorkingDay && !weekend;
            // Saisie "exceptionnelle" = jour non travaillé / férié / congé.
            // Autorisée, mais confirmée par une popup.
            const exceptionalReason = isPartTimeDay
              ? 'un jour non travaillé (temps partiel)'
              : !isWorkingDay
              ? 'un jour non travaillé (week-end)'
              : isHoliday
              ? `un jour férié (${holiday.name ?? 'férié'})`
              : isTimeOff
              ? `un jour de congé (${dayTimeOffs[0].timeOffType?.name ?? 'congé'}${
                  dayTimeOffs[0].status === 'PENDING' ? ', en attente' : ''
                })`
              : null;
            // Le visuel du congé est porté par le bloc congé (ci-dessous) ;
            // la cellule garde l'overlay hachuré des jours fériés / temps partiel.
            const baseBg = isHoliday
              ? 'rgba(120,120,120,0.10)'
              : isToday
              ? 'rgba(50, 100, 150, 0.08)'
              : weekend || isPartTimeDay
              ? 'var(--glass)'
              : 'transparent';
            const bg = isHoliday
              ? `repeating-linear-gradient(45deg, rgba(0,0,0,0.07), rgba(0,0,0,0.07) 4px, transparent 4px, transparent 8px), ${baseBg}`
              : isPartTimeDay
              ? `repeating-linear-gradient(45deg, rgba(0,0,0,0.22), rgba(0,0,0,0.22) 5px, transparent 5px, transparent 10px), var(--glass)`
              : baseBg;
            // Case cliquable en création : mode édition, non verrouillée,
            // sans allocation/log ce jour, et personne saisissable.
            // Les jours exceptionnels restent cliquables (avec confirm).
            const cellLocked = isDateLocked(iso, person.id);
            const canCreateAny =
              mode === 'edit' && !cellLocked && canMutateSquare({ personId: person.id });
            // Clic sur TOUT l'espace libre de la cellule (ticket #44) :
            // les squares captent eux-mêmes leurs clics, donc un clic qui
            // atteint ce fond est forcément un clic dans le vide de la
            // journée — qu'elle soit vide ou déjà partiellement remplie.
            // La bande « + » du bas reste affichée comme repère, et sert
            // toujours de cible quand la journée est pleine (8 h).
            const canCreateHere = canCreateAny;
            return (
              <div
                key={iso}
                data-is-holiday={isHoliday ? 'true' : 'false'}
                data-is-timeoff={isTimeOff ? 'true' : 'false'}
                title={
                  isHoliday
                    ? `${holiday.name ?? 'Jour férié'} (${holiday.regionCode ? 'régional' : 'national'})`
                    : isPartTimeDay
                    ? 'Temps partiel — jour non travaillé'
                    : canCreateHere
                    ? 'Cliquer dans le vide de la journée pour ajouter une saisie'
                    : undefined
                }
                onClick={canCreateHere ? () => handleBlankCellClick(person.id, iso, exceptionalReason) : undefined}
                style={{
                  position: 'absolute',
                  left: dayIndex * dayWidth,
                  top: 0,
                  width: dayWidth,
                  height: personRowHeight,
                  borderRight: '1px solid var(--glass-border)',
                  background: bg,
                  boxSizing: 'border-box',
                  cursor: canCreateHere ? CURSOR_EDIT_CREATE : 'default',
                  // Surlignage de la cellule cible pendant un drag-to-move.
                  ...(dragMove && dragMove.personId === person.id && dragMove.targetDayIndex === dayIndex
                    ? {
                        outline: `2px dashed ${dragMove.valid ? 'var(--accent)' : 'var(--danger)'}`,
                        outlineOffset: '-2px',
                        background: dragMove.valid
                          ? 'rgba(50, 100, 150, 0.10)'
                          : 'rgba(220, 38, 38, 0.10)',
                      }
                    : null),
                }}
              >
                {/* Bande blanche réservée en bas : toujours disponible
                    pour créer une imputation sur la journée, même quand
                    des allocations remplissent déjà la cellule. */}
                {canCreateAny ? (
                  <div
                    title="Cliquer pour ajouter une imputation sur ce jour"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBlankCellClick(person.id, iso, exceptionalReason);
                    }}
                    style={{
                      position: 'absolute',
                      left: 2,
                      right: 2,
                      bottom: 2,
                      height: BLANK_CREATE_STRIP_PX - 4,
                      borderRadius: 'var(--radius-sm)',
                      border: '1px dashed var(--glass-border)',
                      background: 'var(--glass)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                      color: 'var(--text-muted)',
                      boxSizing: 'border-box',
                      cursor: CURSOR_EDIT_CREATE,
                      zIndex: 7,
                    }}
                  >
                    +
                  </div>
                ) : null}
              </div>
            );
          })}

          {/* Repère de dépassement façon Float : ligne rouge au seuil
              de 8h + zone rouge translucide sur le surplus (par-dessus
              les squares, sans bloquer les clics). */}
          {byDay
            ? Array.from(byDay.entries()).flatMap(([dayIndex, squares]) => {
                if (dayIndex < firstVisibleDay - 1 || dayIndex > lastVisibleDay) return [];
                const { totalPx, totalHours: dayHours, capacityLinePx } = layoutDaySquares(
                  squares,
                  rowPxPerHour,
                );
                if (dayHours <= CAPACITY_HOURS || capacityLinePx == null) return [];
                const lineTop = 6 + capacityLinePx;
                const zoneH = Math.max(0, totalPx - capacityLinePx);
                const overLabel = formatHHMM(dayHours - CAPACITY_HOURS);
                return [
                  <div
                    key={`overflow-${dayIndex}`}
                    data-row-type="overflow"
                    title={`Dépassement : +${overLabel} au-delà de 8h (total ${formatHHMM(dayHours)})`}
                    style={{
                      position: 'absolute',
                      left: dayIndex * dayWidth,
                      top: lineTop,
                      width: dayWidth,
                      height: zoneH,
                      background: 'rgba(220, 38, 38, 0.20)',
                      borderTop: '2px solid var(--danger)',
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                      zIndex: 6,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#fff',
                        background: 'var(--danger)',
                        borderRadius: '0 0 0 4px',
                        padding: '1px 4px',
                        lineHeight: 1.2,
                      }}
                    >
                      +{overLabel}
                    </span>
                  </div>,
                ];
              })
            : null}

          {/* Squares (un par jour ouvré). Empilement VERTICAL quand
              plusieurs affaires le même jour : chaque square occupe une
              tranche de la hauteur de ligne. */}
          {byDay
            ? Array.from(byDay.entries()).flatMap(([dayIndex, squares]) => {
                if (dayIndex < firstVisibleDay - 1 || dayIndex > lastVisibleDay) return [];
                const left = dayIndex * dayWidth + 2;
                const width = dayWidth - 4;
                // Layout partagé : chaque square fait au moins MIN_SQUARE_PX
                // (1h lisible), empilé par hauteur rendue. La hauteur de
                // ligne a déjà été calée sur le jour le plus chargé.
                const { items: dayItems } = layoutDaySquares(squares, rowPxPerHour);
                return dayItems.map(({ sq, topPx: itemTop, heightPx: itemHeight }) => {
                  const topPx0 = 6 + itemTop;
                  const heightPx0 = Math.max(MIN_SQUARE_PX, itemHeight - SQUARE_GAP_PX);
                  // ----- Bloc CONGÉ (info, non validable) -----
                  if (sq.kind === 'timeoff') {
                    const toRaw = sq.timeOffType?.colorHex;
                    const toColor = toRaw
                      ? (toRaw.startsWith('#') ? toRaw : `#${toRaw}`)
                      : 'var(--warning)';
                    const compactTo = heightPx0 < 30;
                    // Une absence bornée à la demi-journée n'est pas
                    // une « Journée » : le libellé suit la fenêtre.
                    const halfDay = sq.fullDay && (sq.coverage ?? 1) < 1;
                    // Demande encore en attente de validation : bloc en
                    // pointillés et plus translucide (cohérent Schedule).
                    const toPending = sq.status === 'PENDING';
                    const hoursLabel = sq.fullDay
                      ? (halfDay ? 'Demi-journée' : 'Journée')
                      : sq.hoursPerDay > 0
                      ? formatHHMM(sq.hoursPerDay)
                      : 'Partiel';
                    return (
                      <div
                        key={sq.key}
                        data-row-type="timeoff"
                        title={`Congé : ${sq.name}${
                          sq.fullDay
                            ? halfDay
                              ? ' (demi-journée)'
                              : ' (journée entière)'
                            : ' (partiel)'
                        }${toPending ? ' — en attente de validation' : ''}`}
                        style={{
                          position: 'absolute',
                          left,
                          top: topPx0,
                          width,
                          height: heightPx0,
                          background: toPending
                            ? `repeating-linear-gradient(45deg, ${translucent(toColor, 0.22)}, ${translucent(toColor, 0.22)} 5px, ${translucent(toColor, 0.1)} 5px, ${translucent(toColor, 0.1)} 10px)`
                            : `repeating-linear-gradient(45deg, ${translucent(toColor, 0.5)}, ${translucent(toColor, 0.5)} 5px, ${translucent(toColor, 0.28)} 5px, ${translucent(toColor, 0.28)} 10px)`,
                          border: toPending ? `1px dashed ${toColor}` : `1px solid ${toColor}`,
                          borderRadius: 'var(--radius-sm)',
                          color: 'var(--text)',
                          padding: compactTo ? '0 5px' : '3px 5px',
                          display: 'flex',
                          flexDirection: compactTo ? 'row' : 'column',
                          justifyContent: compactTo ? 'space-between' : 'flex-start',
                          alignItems: compactTo ? 'center' : 'flex-start',
                          gap: compactTo ? 4 : 2,
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                          cursor: 'default',
                          zIndex: 5,
                        }}
                      >
                        <span style={{ fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: compactTo ? 1 : 'none', width: compactTo ? 'auto' : '100%' }}>
                          {sq.name}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 600, flexShrink: 0, opacity: 0.85 }}>
                          {toPending ? `${hoursLabel} • en attente` : hoursLabel}
                        </span>
                      </div>
                    );
                  }
                  const lt = findLoggedTime(sq);
                  const validated = Boolean(lt);
                  const locked = isDateLocked(sq.iso, sq.personId);
                  const readOnly = !canMutateSquare(sq);
                  const busy = Boolean(busyKeys[sq.key]);
                  const color = affaireColor(sq.affaire);
                  const topPx = topPx0;
                  const heightPx = heightPx0;
                  const compact = heightPx < 30;
                  const isHoursDragged = Boolean(validated && hoursDrag && hoursDrag.ltId === lt.id);
                  const displayHours = isHoursDragged
                    ? hoursDrag.currentHours
                    : validated
                    ? Number(lt.hours)
                    : sq.hoursPerDay;
                  // Une case validée affiche la tâche de SA saisie, qui
                  // peut différer de celle prévue au planning.
                  const shownTask = validated ? (lt.task ?? null) : sq.task;
                  // Texte additionnel (tâche / notes / phase) visible quand
                  // la densité offre assez de hauteur.
                  const noteText = (validated ? lt?.notes : sq.notes) || '';
                  const extraText =
                    [sq.name, noteText].filter(Boolean).join(' — ') || sq.phase?.name || '';
                  const showExtra = !compact && heightPx >= 50 && Boolean(extraText);
                  // Ticket #100 — saisie hors du filtre actif : grisée, inerte.
                  const outOfFilter = Boolean(sq.outOfFilter);
                  const canDragMove = validated && !readOnly && !locked && !outOfFilter;
                  // Ticket #48 — ajustement des heures : saisies existantes
                  // seulement, hors affaires fermées aux imputations.
                  const canAdjustHours = canDragMove && !busy && !sq.affaire?.planningOnly;
                  const isDragged = dragMove && dragMove.key === sq.key;
                  return (
                    <button
                      key={sq.key}
                      type="button"
                      data-validated={validated ? 'true' : 'false'}
                      data-locked={locked ? 'true' : 'false'}
                      onPointerDown={canDragMove ? (e) => onSquarePointerDown(e, sq, lt) : undefined}
                      onPointerMove={canDragMove ? onSquarePointerMove : undefined}
                      onPointerUp={canDragMove ? onSquarePointerUp : undefined}
                      onClick={() => {
                        if (justDraggedRef.current) {
                          justDraggedRef.current = false;
                          return;
                        }
                        handleSquareClick(sq);
                      }}
                      disabled={busy}
                      title={
                        `${sq.affaire?.name ?? 'Affaire'}` +
                        `${shownTask ? ' · ' + shownTask.name : ''}` +
                        `${sq.phase ? ' · ' + sq.phase.name : ''}\n` +
                        `${sq.iso} — ${validated ? formatHHMM(Number(lt.hours)) + ' saisi' : formatHHMM(sq.hoursPerDay) + ' prévu'}` +
                        // Ticket #80 — la case tronque le commentaire : le
                        // texte complet est lisible au survol.
                        (extraText ? `\n\n${extraText}` : '') +
                        (locked ? '\n(période verrouillée)' : '') +
                        (sq.affaire?.planningOnly ? '\n(planification uniquement — imputations fermées)' : '') +
                        (outOfFilter ? '\n(hors du filtre actif)' : '')
                      }
                      style={{
                        position: 'absolute',
                        left,
                        top: topPx,
                        width,
                        height: heightPx,
                        background: outOfFilter
                          ? 'var(--glass)'
                          : validated
                          ? color
                          : translucent(color, 0.18),
                        border: outOfFilter
                          ? '1px dashed var(--glass-border)'
                          : validated
                          ? `1px solid ${color}`
                          : `2px dashed ${color}`,
                        borderRadius: 'var(--radius-sm)',
                        color: outOfFilter
                          ? 'var(--text-muted)'
                          : validated
                          ? readableTextOn(color) ?? '#fff'
                          : 'var(--text)',
                        padding: compact ? '0 5px' : '3px 5px',
                        display: 'flex',
                        flexDirection: compact ? 'row' : 'column',
                        justifyContent: compact ? 'space-between' : 'flex-start',
                        alignItems: compact ? 'center' : 'flex-start',
                        gap: compact ? 4 : 2,
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                        cursor: isDragged
                          ? 'move'
                          : outOfFilter
                          ? 'default'
                          : locked
                          ? 'not-allowed'
                          : mode === 'edit'
                          ? readOnly
                            ? 'default'
                            : validated
                            ? CURSOR_BY_MODE.edit
                            : CURSOR_EDIT_CREATE
                          : readOnly
                          ? 'default'
                          : modeCursor,
                        opacity: outOfFilter ? 0.6 : locked ? 0.5 : busy ? 0.6 : isDragged ? 0.85 : 1,
                        zIndex: isDragged || isHoursDragged ? 20 : 4,
                        transform: isDragged ? `translateX(${dragMove.deltaPx}px)` : undefined,
                        boxShadow: isDragged
                          ? dragMove.valid
                            ? '0 0 0 2px var(--accent), 0 6px 16px rgba(0,0,0,0.35)'
                            : '0 0 0 2px var(--danger), 0 6px 16px rgba(0,0,0,0.35)'
                          : undefined,
                        touchAction: canDragMove ? 'none' : undefined,
                        textAlign: 'left',
                        textShadow: validated && !outOfFilter ? '0 1px 2px rgba(0,0,0,0.35)' : 'none',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          flex: compact ? 1 : 'none',
                          width: compact ? 'auto' : '100%',
                        }}
                      >
                        {compact
                          ? sq.affaire?.code || sq.affaire?.name || ''
                          : `${sq.affaire?.name || sq.affaire?.code || ''}${
                              shownTask ? ` · ${shownTask.name}` : ''
                            }`}
                      </span>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          fontSize: 11,
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {validated ? <Check size={11} /> : null}
                        {displayHours > 0 ? formatHHMM(displayHours) : ''}
                      </span>
                      {showExtra ? (
                        <span
                          style={{
                            fontSize: 10,
                            opacity: 0.85,
                            width: '100%',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            whiteSpace: 'normal',
                            lineHeight: 1.25,
                            wordBreak: 'break-word',
                          }}
                        >
                          {extraText}
                        </span>
                      ) : null}
                      {/* Ticket #48 — poignée basse : ajuste les heures
                          de la saisie. Le clic qui termine le geste ne
                          doit pas ouvrir la modale de la case. */}
                      {canAdjustHours ? (
                        <span
                          aria-label="Ajuster les heures saisies"
                          title="Glisser pour ajuster les heures saisies"
                          onPointerDown={(e) => onHoursHandlePointerDown(e, sq, lt, rowPxPerHour)}
                          onPointerMove={onHoursHandlePointerMove}
                          onPointerUp={onHoursHandlePointerUp}
                          onPointerCancel={onHoursHandlePointerUp}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 6,
                            cursor: 'ns-resize',
                            touchAction: 'none',
                            background: isHoursDragged
                              ? 'rgba(255,255,255,0.7)'
                              : 'rgba(255,255,255,0.25)',
                            zIndex: 1,
                          }}
                        />
                      ) : null}
                    </button>
                  );
                });
              })
            : null}
        </div>
      </div>
    );
  }),
    [
      busyKeys,
      canMutateSquare,
      dayWidth,
      dragMove,
      findLoggedTime,
      firstVisibleDay,
      firstVisibleRow,
      handleBlankCellClick,
      handleSquareClick,
      holidaySetByPerson,
      hoursDrag,
      isAdmin,
      isDateLocked,
      lastVisibleDay,
      lastVisibleRow,
      layoutDaySquares,
      mode,
      modeCursor,
      onHoursHandlePointerDown,
      onHoursHandlePointerMove,
      onHoursHandlePointerUp,
      onSquarePointerDown,
      onSquarePointerMove,
      onSquarePointerUp,
      period.from,
      persons,
      rowLayout.heights,
      rowLayout.pxPerHourByRow,
      rowLayout.tops,
      squaresByPerson,
      timeOffByPerson,
      todayIndex,
      totalDays,
      workingMaskByPerson,
    ],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        background: 'var(--bg)',
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 24px',
          borderBottom: '1px solid var(--glass-border)',
          background: 'var(--card-bg)',
          flexWrap: 'wrap',
        }}
      >
        <CalendarDays size={20} color="var(--accent)" />
        <h1 style={{ margin: 0, fontSize: 18, color: 'var(--text)' }}>Validation des temps</h1>

        {/* Toggle Tableau | Calendrier — ticket #58.
            La pastille active utilise `--accent` (#326496) et NON `--primary`
            (#2a3147) : sur le fond sombre `--bg` (#0f172a), `--primary` est
            trop proche du fond pour que la sélection se voie, et le même
            toggle rendu par LogtimeView utilise déjà `--accent`. Les deux
            toggles doivent rester alignés — c'est le même contrôle, rendu par
            deux composants selon le mode courant. */}
        <div style={{ display: 'inline-flex', gap: 6, marginLeft: 8 }}>
          <button
            type="button"
            onClick={() => onChangeViewMode?.('table')}
            style={{
              ...pillBtn,
              background: viewMode === 'table' ? 'var(--accent)' : 'var(--glass)',
              color: viewMode === 'table' ? '#fff' : 'var(--text)',
            }}
          >
            <Table size={13} /> Tableau
          </button>
          <button
            type="button"
            onClick={() => onChangeViewMode?.('calendar')}
            style={{
              ...pillBtn,
              background: viewMode === 'calendar' ? 'var(--accent)' : 'var(--glass)',
              color: viewMode === 'calendar' ? '#fff' : 'var(--text)',
            }}
          >
            <CalendarDays size={13} /> Calendrier
          </button>
        </div>

        {/* Zoom : Semaine (2 sem) | Mois (~5 sem) */}
        <div style={{ display: 'inline-flex', gap: 4, marginLeft: 8 }}>
          {['week', 'month'].map((z) => (
            <button
              key={z}
              type="button"
              onClick={() => setZoom(z)}
              style={{
                background: zoom === z ? 'var(--accent)' : 'transparent',
                color: zoom === z ? '#fff' : 'var(--text)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-pill)',
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {z === 'week' ? 'Semaine' : 'Mois'}
            </button>
          ))}
        </div>

        {/* Densité d'affichage (hauteur des lignes) */}
        <div style={{ marginLeft: 8 }}>
          <DensityPicker value={density} onChange={changeDensity} />
        </div>

        {/* Périmètre affiché — tags cliquables (ticket #45 : « Tout le monde »
            ouvert à tous, « Mon équipe » réservé à qui en a une) */}
        <div style={{ display: 'inline-flex', gap: 6, marginLeft: 8 }}>
          {[
            { key: 'self', label: 'Mes temps', show: true },
            { key: 'team', label: 'Mon équipe', show: isManager || isAdmin },
            { key: 'all', label: 'Tout le monde', show: true },
          ]
            .filter((s) => s.show)
            .map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setScope(s.key)}
                title={`Afficher : ${s.label}`}
                style={{
                  ...pillBtn,
                  background: scope === s.key ? 'var(--accent)' : 'var(--glass)',
                  color: scope === s.key ? '#fff' : 'var(--text)',
                }}
              >
                {s.label}
              </button>
            ))}
        </div>

        {/* Filtre facetté — PARTAGÉ avec le Schedule (scope `schedule`) */}
        <div style={{ marginLeft: 12, display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          <FilterBar scope="schedule" />
        </div>

        {/* Ticket #34 — flexShrink: 0 : la navigation de période ne doit jamais
            être compressée par une chip de filtre au libellé long. */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            marginLeft: 'auto',
            flexShrink: 0,
          }}
        >
          <button type="button" onClick={() => shift(-1)} style={navBtn} title="Période précédente">
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={gotoToday}
            style={{ ...navBtn, padding: '4px 12px', width: 'auto' }}
            title="Revenir à aujourd'hui"
          >
            Aujourd&apos;hui
          </button>
          <button type="button" onClick={() => shift(1)} style={navBtn} title="Période suivante">
            <ChevronRight size={16} />
          </button>
          {/* Largeur FIXE : un libellé de largeur variable décalait les flèches
              et « Aujourd'hui » à chaque clic → clic par erreur. */}
          <span
            style={{
              marginLeft: 12,
              color: 'var(--text-muted)',
              fontSize: 13,
              whiteSpace: 'nowrap',
              width: '16.5em',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtPeriodTitle(visibleFrom, visibleTo)}
          </span>
        </div>
      </div>

      {/* Légende */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '6px 24px',
          fontSize: 12,
          color: 'var(--text-muted)',
          borderBottom: '1px solid var(--glass-border)',
          background: 'var(--bg)',
          flexWrap: 'wrap',
        }}
      >
        <Info size={13} />
        {/* Mode courant — changeable par clic droit sur la grille */}
        <ToolModeBadge modes={MODES} mode={mode} />
        <span style={{ fontStyle: 'italic' }}>(clic droit pour changer de mode)</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              display: 'inline-block',
              width: 16,
              height: 12,
              borderRadius: 3,
              background: translucent('#326496', 0.18),
              border: '2px dashed var(--accent)',
            }}
          />
          Prévu (non validé)
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              display: 'inline-block',
              width: 16,
              height: 12,
              borderRadius: 3,
              background: 'var(--accent)',
            }}
          />
          Validé (heures saisies)
        </span>
        {schedule.lock ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--warning)' }}>
            <Lock size={13} /> Verrouillé jusqu&apos;au {schedule.lock.lockDate}
          </span>
        ) : null}
      </div>

      {error ? <div style={{ padding: '12px 24px', color: 'var(--danger)' }}>{error}</div> : null}

      {/* Container scrollable */}
      <div style={{ position: 'relative', height: 0 }}>
        <TimelineProgress active={fetching && !loading} />
      </div>
      <div ref={containerRef} className="scroll-visible" {...containerProps} onContextMenu={openContextMenu} style={{ flex: 1, minHeight: 0, overflow: 'auto', position: 'relative' }}>
        {/* Header sticky */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            display: 'flex',
            background: 'var(--card-bg)',
            borderBottom: '1px solid var(--glass-border)',
            height: HEADER_HEIGHT_PX,
            width: LEFT_COL_WIDTH_PX + totalDays * dayWidth,
          }}
        >
          <div
            style={{
              position: 'sticky',
              left: 0,
              zIndex: 11,
              width: LEFT_COL_WIDTH_PX,
              flex: '0 0 auto',
              background: 'var(--card-bg)',
              borderRight: '1px solid var(--glass-border)',
              display: 'flex',
              alignItems: 'center',
              padding: '0 12px',
              fontSize: 12,
              color: 'var(--text-muted)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            Personnes ({persons.length})
          </div>
          <div style={{ position: 'relative', flex: '1 0 auto', width: totalDays * dayWidth, height: HEADER_HEIGHT_PX }}>
            {Array.from({ length: Math.max(0, lastVisibleDay - firstVisibleDay) }).map((_, i) => {
              const dayIndex = firstVisibleDay + i;
              const d = addDays(period.from, dayIndex);
              const isToday = dayIndex === todayIndex;
              const weekend = isWeekend(d);
              const dow = (d.getDay() + 6) % 7;
              // Ticket #87 — repère de mois : au 1er du mois et sur le premier
              // jour à l'écran, pour savoir où l'on est après navigation.
              const showMonth = d.getDate() === 1 || dayIndex === visibleFromIdx;
              return (
                <div
                  key={toISODate(d)}
                  data-is-today={isToday ? 'true' : 'false'}
                  style={{
                    position: 'absolute',
                    left: dayIndex * dayWidth,
                    top: 0,
                    width: dayWidth,
                    height: HEADER_HEIGHT_PX,
                    borderRight: '1px solid var(--glass-border)',
                    background: isToday ? 'var(--accent)' : weekend ? 'var(--glass)' : 'transparent',
                    color: isToday ? '#fff' : weekend ? 'var(--text-muted)' : 'var(--text)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    lineHeight: 1.2,
                    boxSizing: 'border-box',
                  }}
                  title={`${DAYS_FR_LONG[dow]} ${d.getDate()} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`}
                >
                  <span style={{ opacity: 0.7 }}>{DAYS_FR_SHORT[dow]}</span>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{d.getDate()}</span>
                  {showMonth ? (
                    <span style={{ fontSize: 10, fontWeight: 600, opacity: 0.8, whiteSpace: 'nowrap' }}>
                      {MONTHS_FR[d.getMonth()]}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        {/* Corps (le onContextMenu est sur le container scrollable parent pour
            couvrir aussi la zone vide sous les lignes) */}
        <div
          style={{
            position: 'relative',
            width: LEFT_COL_WIDTH_PX + totalDays * dayWidth,
            height: rowLayout.totalHeight,
            cursor: modeCursor,
          }}
        >
          {loading ? (
            <div style={{ position: 'absolute', left: LEFT_COL_WIDTH_PX, top: 0, padding: 20, color: 'var(--text-muted)' }}>
              Chargement…
            </div>
          ) : null}

          {rowsElements}

          {!loading && totalRows === 0 ? (
            <div style={{ position: 'absolute', left: LEFT_COL_WIDTH_PX + 20, top: 20, color: 'var(--text-muted)' }}>
              Aucune personne à afficher.
            </div>
          ) : null}
        </div>
      </div>

      {/* Édition Person depuis le clic sur le nom (ADMIN) */}
      <PersonFormModal
        open={Boolean(editPersonId)}
        personId={editPersonId}
        onClose={() => setEditPersonId(null)}
        onSaved={() => {
          setEditPersonId(null);
          setReloadKey((k) => k + 1);
        }}
      />

      {/* Modale d'édition (mode Modification) */}
      {editTarget ? (
        <EditDayModal
          target={editTarget}
          isAdmin={isAdmin}
          myPersonId={myPersonId}
          minHours={minHours}
          onClose={() => setEditTarget(null)}
          onCreated={(lt) => {
            addLoggedTime(lt);
            setEditTarget(null);
          }}
          onUpdated={(lt) => {
            replaceLoggedTime(lt);
            // Tickets #96 / #98 — une saisie déplacée sur une autre affaire
            // peut sortir du filtre actif : seul le serveur sait la marquer.
            if (filterQuery && lt.affaireId !== editTarget.loggedTime?.affaireId) {
              setReloadKey((k) => k + 1);
            }
            setEditTarget(null);
          }}
          onArchived={(id) => {
            removeLoggedTime(id);
            setEditTarget(null);
          }}
          onError={showToast}
        />
      ) : null}

      {/* Menu contextuel (clic droit) — changer de mode */}
      <ToolModeContextMenu
        position={contextMenu}
        modes={MODES}
        mode={mode}
        onChange={setMode}
        onClose={closeContextMenu}
      />

      {/* Toast */}
      {toast ? (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: toast.kind === 'error' ? 'var(--danger)' : 'var(--success)',
            color: '#fff',
            padding: '10px 18px',
            borderRadius: 'var(--radius-pill)',
            boxShadow: 'var(--shadow-card)',
            zIndex: 1000,
            fontSize: 13,
          }}
        >
          {toast.msg}
        </div>
      ) : null}
    </div>
  );
}

// ---------- Modale d'édition d'un jour ----------
function EditDayModal({ target, isAdmin = false, myPersonId = null, minHours = 1, onClose, onCreated, onUpdated, onArchived, onError }) {
  // `locked` = verrou comptable ; `readOnly` = pas de droit d'écriture sur
  // cette personne/affaire. Les deux rendent la modale non éditable, mais le
  // message doit dire lequel (ticket #29, retour du 14/09).
  const locked = Boolean(target.locked) || Boolean(target.readOnly);
  const lockedByPeriod = Boolean(target.locked);
  const square = target.square ?? null;
  const loggedTime = target.loggedTime ?? null;
  const blank = target.blank ?? null;
  const isBlank = Boolean(blank);

  const personId = square?.personId ?? blank?.personId ?? null;
  const iso = square?.iso ?? blank?.iso ?? '';

  // Ticket #99 — une saisie libre démarre à une journée (8h), le cas le plus
  // courant ; un jour planifié reprend la prévision.
  const [hours, setHours] = useState(
    loggedTime
      ? Number(loggedTime.hours)
      : square && square.hoursPerDay > 0
      ? square.hoursPerDay
      : CAPACITY_HOURS,
  );
  const [phaseId, setPhaseId] = useState(loggedTime?.phaseId ?? square?.phaseId ?? '');
  const [notes, setNotes] = useState(loggedTime?.notes ?? '');
  // Ticket #80 — note de l'allocation, affichée si elle diffère de la saisie.
  const planningNote =
    square?.notes && square.notes.trim() !== (loggedTime?.notes ?? '').trim()
      ? square.notes
      : '';
  // Ticket #76 — la tâche est proposée sur toute case, planifiée ou non, comme
  // dans le Tableau. Pré-remplie avec celle de la saisie, à défaut celle de
  // l'allocation : valider une case planifiée ne doit pas perdre sa tâche.
  const initialTask = loggedTime
    ? { taskId: loggedTime.taskId ?? null, taskName: loggedTime.task?.name ?? null }
    : { taskId: square?.taskId ?? null, taskName: square?.task?.name ?? null };
  const [task, setTask] = useState(initialTask);
  const [phases, setPhases] = useState([]);
  const [saving, setSaving] = useState(false);
  // Erreur affichée DANS la modale (sinon le toast page passe sous le backdrop
  // flouté et reste illisible).
  const [formError, setFormError] = useState(null);

  // Sélecteur d'affaire éditable en création (case vide OU square proposé).
  // En création depuis un square, on pré-remplit avec l'affaire du planning
  // mais l'utilisateur peut en choisir une autre.
  const isCreate = !loggedTime;
  // Affaire en planification uniquement : pas d'imputation possible dessus.
  // Depuis un square planifié sur une telle affaire, l'utilisateur doit choisir
  // une autre affaire (pas de pré-remplissage) ; une saisie existante dessus
  // (import Float antérieur) n'est plus modifiable, seulement supprimable.
  const squareAffaireClosed = Boolean(square?.affaire?.planningOnly);
  const loggedTimeClosed = Boolean(loggedTime?.affaire?.planningOnly);
  const [affaireId, setAffaireId] = useState(
    loggedTime ? loggedTime.affaireId : squareAffaireClosed ? '' : square?.affaireId ?? '',
  );
  const [affaires, setAffaires] = useState([]);
  const effectiveAffaireId = loggedTime
    ? affaireId || loggedTime.affaireId
    : affaireId || (squareAffaireClosed ? null : square?.affaireId) || null;
  // L'affaire choisie diffère-t-elle de celle proposée par le planning ?
  const affaireOverridden = Boolean(square) && affaireId && affaireId !== square.affaireId;
  // Tickets #96 / #98 — une saisie existante peut changer d'affaire (avant, il
  // fallait la supprimer puis la recréer).
  const canChangeAffaire = isCreate || (!locked && !loggedTimeClosed);
  const affaireChanged = Boolean(loggedTime) && effectiveAffaireId !== loggedTime.affaireId;
  // L'affaire actuelle de la saisie reste proposée même si elle ne figure pas
  // dans la liste (saisie modifiée par le responsable d'affaire, par exemple).
  const affaireOptions = useMemo(() => {
    const current = loggedTime?.affaire;
    if (!current || affaires.some((a) => a.id === current.id)) return affaires;
    return [current, ...affaires];
  }, [affaires, loggedTime]);

  // Charge la liste des affaires sélectionnables (création, ou modification
  // d'une saisie encore éditable).
  useEffect(() => {
    if (!canChangeAffaire) return undefined;
    let cancelled = false;
    apiAffaires
      .list('active', null, {
        // Ticket #29 — quand on saisit pour quelqu'un d'autre (subordonné),
        // la liste proposée doit être celle des affaires de CETTE personne.
        ...(personId && personId !== myPersonId
          ? { forPersonId: personId }
          : { mineOnly: !isAdmin }),
      })
      .then((res) => {
        if (cancelled) return;
        // Exclut les affaires en planification uniquement (imputations fermées).
        const list = (res.items ?? res ?? []).filter((a) => !a.planningOnly);
        setAffaires(list);
        // synotis-support#8 — une création sans affaire proposée par le
        // planning reprend la dernière affaire saisie, si elle est offerte.
        if (isCreate) {
          const last = lastAffaireIn(list);
          if (last) setAffaireId((prev) => prev || last);
        }
      })
      .catch(() => {
        if (!cancelled) setAffaires([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canChangeAffaire, isAdmin, personId, myPersonId]);

  // Phases de l'affaire effective (reset à chaque changement d'affaire).
  useEffect(() => {
    if (!effectiveAffaireId) {
      setPhases([]);
      return undefined;
    }
    let cancelled = false;
    apiPhases
      .list(effectiveAffaireId, 'active')
      .then((res) => {
        if (!cancelled) setPhases(res.items ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setPhases([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveAffaireId]);

  const submit = async () => {
    if (locked || loggedTimeClosed) return;
    setFormError(null);
    if (isCreate && !effectiveAffaireId) {
      setFormError('Sélectionner une affaire');
      return;
    }
    if (!Number.isFinite(hours) || hours <= 0) {
      setFormError('Saisir un nombre d\'heures > 0');
      return;
    }
    if (minHours > 0 && hours < minHours) {
      setFormError(`Durée minimale de saisie : ${formatHHMM(minHours)}`);
      return;
    }
    setSaving(true);
    // `taskId` désigne une tâche existante, `taskName` une tâche à créer ;
    // `{ taskId: null }` détache. Même contrat que le Tableau.
    const taskPayload = task.taskId
      ? { taskId: task.taskId }
      : task.taskName
      ? { taskName: task.taskName }
      : { taskId: null };
    try {
      if (loggedTime) {
        const taskChanged =
          affaireChanged ||
          (task.taskId ?? null) !== (initialTask.taskId ?? null) ||
          (!task.taskId && (task.taskName ?? null) !== (initialTask.taskName ?? null));
        const newAffaire = affaireChanged
          ? affaireOptions.find((a) => a.id === effectiveAffaireId)
          : null;
        const updated = await apiLoggedTime.update(loggedTime.id, {
          hours,
          phaseId: phaseId || null,
          notes: notes || null,
          // La tâche n'est envoyée que si elle a changé : le serveur la
          // résout (et peut la créer) à chaque envoi. Changer d'affaire
          // l'envoie toujours — l'ancienne tâche appartient à l'ancienne
          // affaire.
          ...(taskChanged ? taskPayload : {}),
          // Tickets #96 / #98 — la saisie devient manuelle sur sa nouvelle
          // affaire ; le lien à l'allocation est conservé pour que le jour
          // planifié d'origine reste masqué (cf. squaresByPerson).
          ...(affaireChanged
            ? {
                affaireId: effectiveAffaireId,
                source: 'manual',
                billable: !newAffaire?.nonBillable,
              }
            : {}),
        });
        if (affaireChanged) rememberLastAffaire(effectiveAffaireId);
        onUpdated?.(updated);
      } else {
        const created = await apiLoggedTime.create({
          personId,
          affaireId: effectiveAffaireId,
          date: iso,
          hours,
          // Facturable hérité de l'Affaire/allocation — pas de saisie manuelle.
          billable: square && !affaireOverridden ? square.billable : true,
          notes: notes || null,
          // Square planifié non modifié → 'planning' ; case vide ou affaire
          // changée → 'manual'.
          source: square && !affaireOverridden ? 'planning' : 'manual',
          ...(phaseId ? { phaseId } : {}),
          ...(task.taskId || task.taskName ? taskPayload : {}),
          // On conserve le lien vers l'allocation source même si l'affaire a
          // changé : ce log « remplace » l'allocation planifiée, ce qui permet
          // de masquer le square planifié (cf. squaresByPerson).
          ...(square?.allocationId ? { allocationId: square.allocationId } : {}),
        });
        rememberLastAffaire(effectiveAffaireId);
        onCreated?.(created);
      }
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async () => {
    if (locked || !loggedTime) return;
    if (!window.confirm('Supprimer cette saisie ?')) return;
    setSaving(true);
    try {
      await apiLoggedTime.archive(loggedTime.id);
      onArchived?.(loggedTime.id);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const fieldLabel = { fontSize: 12, color: 'var(--text-muted)', marginBottom: 4, display: 'block' };
  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    background: 'var(--glass)',
    color: 'var(--text)',
    border: '1px solid var(--glass-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 13,
    boxSizing: 'border-box',
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`${(loggedTime?.affaire ?? square?.affaire)?.name ?? 'Nouvelle saisie'} — ${iso}`}
      subtitle={loggedTime ? 'Modifier la saisie' : 'Créer la saisie pour ce jour'}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
          <div>
            {loggedTime ? (
              <button
                type="button"
                onClick={doArchive}
                disabled={saving || locked}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'transparent',
                  color: 'var(--danger)',
                  border: '1px solid var(--danger)',
                  borderRadius: 'var(--radius-pill)',
                  padding: '6px 14px',
                  fontSize: 13,
                  cursor: locked ? 'not-allowed' : 'pointer',
                  opacity: locked ? 0.5 : 1,
                }}
              >
                <Trash2 size={14} /> Supprimer
              </button>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'transparent',
                color: 'var(--text)',
                border: '1px solid var(--glass-border)',
                borderRadius: 'var(--radius-pill)',
                padding: '6px 14px',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving || locked || loggedTimeClosed}
              style={{
                background: 'var(--accent)',
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-pill)',
                padding: '6px 16px',
                fontSize: 13,
                fontWeight: 500,
                cursor: saving || locked || loggedTimeClosed ? 'not-allowed' : 'pointer',
                opacity: saving || locked || loggedTimeClosed ? 0.6 : 1,
              }}
            >
              {loggedTime ? 'Enregistrer' : isBlank ? 'Créer' : 'Valider'}
            </button>
          </div>
        </div>
      }
    >
      {locked ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            background: 'var(--glass)',
            border: '1px solid var(--warning)',
            color: 'var(--warning)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          <Lock size={14} />{' '}
          {lockedByPeriod
            ? 'Période verrouillée — lecture seule.'
            : 'Lecture seule — vous n\'avez pas le droit de modifier les saisies de cette personne.'}
        </div>
      ) : null}

      {!locked && (loggedTimeClosed || squareAffaireClosed) ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            background: 'var(--glass)',
            border: '1px solid var(--warning)',
            color: 'var(--warning)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          <Lock size={14} />{' '}
          {loggedTimeClosed
            ? `${PLANNING_ONLY_MSG} — saisie non modifiable.`
            : `${PLANNING_ONLY_MSG} — choisissez une autre affaire.`}
        </div>
      ) : null}

      {formError ? (
        <div
          style={{
            padding: '8px 12px',
            background: 'var(--glass)',
            border: '1px solid var(--danger)',
            color: 'var(--danger)',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 14,
            fontSize: 13,
          }}
        >
          {formError}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {canChangeAffaire ? (
          <div>
            <label style={fieldLabel}>Affaire</label>
            <AffaireFilterableSelect
              affaires={affaireOptions}
              value={effectiveAffaireId ?? ''}
              onChange={(id) => {
                setAffaireId(id);
                setPhaseId('');
                // Une tâche appartient à son affaire : le serveur refuserait
                // de rattacher celle de l'affaire précédente.
                setTask({ taskId: null, taskName: null });
              }}
              disabled={locked}
              autoFocus={isBlank}
              selectStyle={inputStyle}
            />
            {affaireChanged ? (
              <span style={{ fontSize: 11, color: 'var(--warning)' }}>
                La saisie sera déplacée sur cette affaire (phase et tâche à re-sélectionner).
              </span>
            ) : affaireOverridden && isCreate ? (
              <span style={{ fontSize: 11, color: 'var(--warning)' }}>
                Affaire différente du planning — saisie manuelle (non rattachée à l'allocation).
              </span>
            ) : null}
          </div>
        ) : null}

        <div>
          <label style={fieldLabel}>Heures</label>
          <DurationInput
            value={hours}
            onChange={(v) => setHours(v)}
            disabled={locked}
          />
          {square && square.hoursPerDay > 0 ? (
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              Prévu au planning : {formatHHMM(square.hoursPerDay)}
            </span>
          ) : null}
        </div>

        <div>
          <label style={fieldLabel}>Phase</label>
          <select
            value={phaseId ?? ''}
            onChange={(e) => setPhaseId(e.target.value || '')}
            disabled={locked}
            style={inputStyle}
          >
            <option value="">— Aucune —</option>
            {phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={fieldLabel} htmlFor="edit-day-task">Tâche (optionnel)</label>
          <TaskPicker
            inputId="edit-day-task"
            affaireId={effectiveAffaireId}
            value={task}
            onChange={setTask}
            disabled={locked || loggedTimeClosed || !effectiveAffaireId}
            style={inputStyle}
          />
        </div>

        {/* Ticket #80 — commentaire du planning (allocation), jusqu'ici
            visible seulement tronqué dans la case. */}
        {planningNote ? (
          <div>
            <label style={fieldLabel}>Note du planning</label>
            <div
              style={{
                ...inputStyle,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 200,
                overflowY: 'auto',
                color: 'var(--text-muted)',
              }}
            >
              {planningNote}
            </div>
          </div>
        ) : null}

        <div>
          <label style={fieldLabel} htmlFor="edit-day-notes">Notes</label>
          {/* Ticket #80 — hauteur ajustée au texte, et `readOnly` plutôt que
              `disabled` : en lecture seule le commentaire reste lisible en
              entier (défilable, sélectionnable). L'enregistrement est déjà
              bloqué par le bouton désactivé. */}
          <textarea
            id="edit-day-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            readOnly={locked}
            rows={notesRows(notes)}
            style={{
              ...inputStyle,
              resize: 'vertical',
              ...(locked ? { color: 'var(--text-muted)', cursor: 'default' } : {}),
            }}
          />
        </div>
      </div>
    </Modal>
  );
}
